import React, { useEffect, useState, useRef, useCallback, useId } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useLocation, Link } from 'react-router-dom';
import {
    BarChart3, Search, Gauge, AlertTriangle, CheckCircle, XCircle, Lock,
    Monitor, Smartphone, Shield, Eye, FileText, Sparkles,
    Type, AlignLeft, Heading1, ImageIcon, Link2, ArrowRight,
    Globe, BookOpen, Code, Hash, Wrench, Languages, TrendingUp,
    ChevronDown, ChevronUp, ChevronLeft, ChevronRight, MapPin, Users, Star, ArrowUp, MousePointer, X, HelpCircle,
    CalendarCheck, Loader2, ExternalLink
} from 'lucide-react';
import BlurredSection from './components/BlurredSection.jsx';
import GoogleSerpPreview from './components/GoogleSerpPreview.jsx';
import PublicCTA from './components/PublicCTA.jsx';
import { MarcheMetricCards } from './components/SemrushOverview.jsx';

import { API_BASE_URL } from './config/api';

const DEFAULT_CTA_LINK = 'https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ1YMdoBFjSXap-qyyOBfQTMArJj38qXktPRF-KaQqbehje5yDXB2YYL_evmN079g30I8v7V5rsQ';
const PUBLIC_AUDIT_TRACKING_QS_KEY = 'public_audit_tracking_qs';

function getTrackingSearch(search) {
    const src = new URLSearchParams(search || '');
    const out = new URLSearchParams();
    const keepExact = new Set(['gclid', 'fbclid', 'msclkid', 'ttclid']);
    src.forEach((value, key) => {
        const k = String(key || '').toLowerCase();
        if (k.startsWith('utm_') || keepExact.has(k)) out.set(key, value);
    });
    const qs = out.toString();
    return qs ? `?${qs}` : '';
}

function getPersistedTrackingSearch() {
    if (typeof window === 'undefined') return '';
    try {
        const qs = sessionStorage.getItem(PUBLIC_AUDIT_TRACKING_QS_KEY) || '';
        return qs.startsWith('?') ? qs : (qs ? `?${qs}` : '');
    } catch (_) {
        return '';
    }
}

function persistTrackingSearch(search) {
    if (typeof window === 'undefined') return;
    if (!search) return;
    try {
        sessionStorage.setItem(PUBLIC_AUDIT_TRACKING_QS_KEY, search);
    } catch (_) {}
}

const ONBOARDING_STORAGE_KEY = (t) => `pub_onboarding_done_${t}`;
const CONVERSION_MODAL_STORAGE_KEY = (t) => `pub_conversion_modal_shown_${t}`;
const BLOCK_STAGGER_MS = 320;
const PUBLIC_AUDIT_RESULT_META_DESC = "Découvrez le détail de votre audit SEO & GEO : score global, performances, technique, contenu, avec recommandations priorisées par Ascanio.";

function upsertMetaTag(attr, key, content) {
    if (typeof document === 'undefined') return;
    const selector = `meta[${attr}="${key}"]`;
    let el = document.querySelector(selector);
    if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
    }
    el.setAttribute('content', content);
}

// Explications vulgarisées par onglet — ce qu'on analyse et pourquoi c'est important
const TAB_EXPLANATIONS = {
    seo: "Nous analysons les balises essentielles (titre, meta description, H1) et la structure de votre page. C'est ce que Google lit en premier pour comprendre votre contenu et décider de vous afficher dans les résultats de recherche. Un bon SEO signifie plus de visibilité et plus de visiteurs qualifiés.",
    performance: "Nous mesurons la vitesse de chargement de votre site sur desktop et mobile, ainsi que l'accessibilité et les bonnes pratiques. Un site lent fait fuir les visiteurs et Google pénalise les pages trop lentes dans son classement. La performance impacte directement votre conversion et votre référencement.",
    technique: "Nous vérifions la configuration de base : HTTPS, adaptation mobile, indexation, données structurées, canonical, fichier llm.txt. Ces fondations techniques sont indispensables pour que Google puisse explorer et comprendre votre site. Sans elles, même le meilleur contenu restera invisible.",
    marche: "Ces chiffres viennent de Semrush : ils mesurent votre visibilité réelle sur Google — trafic organique (visiteurs trouvés gratuitement via la recherche), mots-clés positionnés, et part payante éventuelle. Ils vous permettent de voir où vous en êtes par rapport à vos concurrents et d'évaluer le potentiel de croissance.",
    visuel: "L'IA analyse visuellement votre page d'accueil à partir d'une capture statique : typographie, couleurs, contrastes, boutons, hiérarchie visuelle et modernité du design. Les animations ne sont pas évaluées (non visibles sur capture). Ces critères impactent la crédibilité perçue et le taux de conversion.",
    ia: "L'IA analyse le contenu de votre page d'accueil pour en déduire votre secteur, votre cible et votre proposition de valeur. Elle identifie des axes d'amélioration pour vous aider à mieux vous positionner et à améliorer votre message.",
};

// Guides pour comprendre les indicateurs de marché (audit gratuit : visites + mots-clés)
const MARCHE_GUIDES = [
    { label: 'Trafic organique', short: 'Visiteurs qui vous trouvent gratuitement sur Google', icon: TrendingUp },
    { label: 'Mots-clés organiques', short: 'Requêtes pour lesquelles vous apparaissez dans les résultats', icon: Hash },
];

// Couleurs pastel par onglet (Synthèse) : SEO, Performance, Technique, Marché, Visuel, IA
const SYNTHESE_TAB_COLORS = ['#5b8fd4', '#5eb89a', '#e5a855', '#5eb8d9', '#e8609a', '#9b7ed9'];

// Recommandations fictives "visibilité LLM" pour l'effet blur + CTA dans la section modèles IA
const FAKE_LLM_RECOMMENDATIONS = [
    { title: "Fichier llm.txt et métadonnées", explanation: "Publier un llm.txt et des métadonnées claires pour que les modèles sachent quand et comment citer votre site." },
    { title: "Contenu factuel et sourcé", explanation: "Privilégier des affirmations vérifiables et des sources pour augmenter la confiance des IA dans vos réponses." },
    { title: "Stratégie de contenu pour les requêtes longue traîne", explanation: "Cibler les questions précises que posent les utilisateurs pour apparaître dans les réponses des assistants." },
];

// Points fictifs pour allonger la liste des recommandations (effet tease sous le blur)
const FAKE_IMPROVEMENTS = [
    { title: "Contacter Ascanio", explanation: "Avoir une entreprise de référencement qui vous accompagne et vous aide à optimiser votre site.", priority: "haute" },
    { title: "Données structurées Schema.org", explanation: "Implémenter les balises JSON-LD pour apparaître en rich snippets et améliorer la visibilité.", priority: "haute" },
    { title: "Vitesse de chargement mobile", explanation: "Réduire le LCP et le CLS sur mobile pour une meilleure expérience utilisateur et un meilleur classement.", priority: "moyenne" },
    { title: "Stratégie de maillage interne", explanation: "Structurer les liens internes pour faciliter le crawl et renforcer l'autorité des pages clés.", priority: "moyenne" },
];

// ── Helper components ────────────────────────────────────────────────────

/** Barre de progression animée (0% → percent) au montage */
function AnimatedProgressBar({ percent, duration = 800, delay = 0, color, className = '' }) {
    const [width, setWidth] = useState(0);
    const target = Math.min(100, Math.max(0, Number(percent) || 0));

    useEffect(() => {
        if (delay > 0) {
            const t = setTimeout(() => {
                let start = null;
                const step = (ts) => {
                    if (start == null) start = ts;
                    const elapsed = ts - start;
                    const progress = Math.min(elapsed / duration, 1);
                    const easeOut = 1 - (1 - progress) ** 3;
                    setWidth(easeOut * target);
                    if (progress < 1) requestAnimationFrame(step);
                };
                requestAnimationFrame(step);
            }, delay);
            return () => clearTimeout(t);
        }
        let start = null;
        const step = (ts) => {
            if (start == null) start = ts;
            const elapsed = ts - start;
            const progress = Math.min(elapsed / duration, 1);
            const easeOut = 1 - (1 - progress) ** 3;
            setWidth(easeOut * target);
            if (progress < 1) requestAnimationFrame(step);
        };
        const id = requestAnimationFrame(step);
        return () => cancelAnimationFrame(id);
    }, [target, duration, delay]);

    return (
        <div className={`h-full rounded-full ${className}`.trim()} style={{ width: `${width}%`, background: color }} />
    );
}

function ScoreGauge({ value, size = 140 }) {
    const score = Math.round(value || 0);
    const radius = (size - 20) / 2;
    const circumference = 2 * Math.PI * radius;
    const targetOffset = circumference - (score / 100) * circumference;
    const [offset, setOffset] = useState(circumference);
    useEffect(() => {
        const id = requestAnimationFrame(() => setOffset(targetOffset));
        return () => cancelAnimationFrame(id);
    }, [targetOffset]);
    const color = score >= 90 ? '#22c55e' : score >= 60 ? '#f59e0b' : '#ef4444';
    const strokeWidth = 8;

    return (
        <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
            <svg width={size} height={size} style={{ transform: 'rotate(-90deg)', position: 'absolute' }}>
                <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#f1f5f9" strokeWidth={strokeWidth} />
                <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth={strokeWidth}
                    strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset}
                    style={{ transition: 'stroke-dashoffset 1s cubic-bezier(0.22, 1, 0.36, 1)' }} />
            </svg>
            <div className="flex flex-col items-center">
                <span className="font-extrabold text-gray-900" style={{ fontSize: size * 0.22 }}>{score}</span>
                <span className="text-gray-400 font-medium" style={{ fontSize: size * 0.1 }}>/100</span>
            </div>
        </div>
    );
}

/** Jauge gamifiée : anneau principal = note globale, petits arcs = sous-scores (SEO, Perf, Tech, Marché, IA) */
function GamifiedScoreGauge({ mainScore, subScores, subColors, subLabels, hoveredIndex, onArcHover, size = 220 }) {
    const score = Math.round(mainScore || 0);
    const cx = size / 2;
    const cy = size / 2;

    // Anneau principal (intérieur)
    const mainStroke = 13;
    const mainRadius = size / 2 - 28; // laisse de la place pour l'anneau ext.

    // Arcs extérieurs (sous-scores)
    const subStroke = 7;
    const subGap = 8; // espace entre bord ext. de l'anneau principal et centre de l'arc
    const subRadius = mainRadius + mainStroke / 2 + subGap + subStroke / 2;

    const mainCirc = 2 * Math.PI * mainRadius;
    const targetMainOffset = mainCirc - (score / 100) * mainCirc;
    const [mainOffset, setMainOffset] = useState(mainCirc);
    useEffect(() => {
        const id = requestAnimationFrame(() => setMainOffset(targetMainOffset));
        return () => cancelAnimationFrame(id);
    }, [targetMainOffset]);
    const mainColor = score >= 90 ? '#22c55e' : score >= 60 ? '#f59e0b' : '#ef4444';

    const N = subScores.length;
    const segGap = 5; // degrés de séparation entre segments
    const segSpan = 360 / N - segGap; // degrés utiles par segment
    const defaultColors = ['#1e40af', '#10b981', '#f59e0b', '#0ea5e9', '#8b5cf6'];
    const colors = subColors ?? defaultColors;

    const toRad = (deg) => (deg * Math.PI) / 180;
    const pt = (deg) => {
        const r = toRad(deg - 90);
        return {
            x: (cx + subRadius * Math.cos(r)).toFixed(2),
            y: (cy + subRadius * Math.sin(r)).toFixed(2),
        };
    };

    return (
        <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
            <svg width={size} height={size} className="absolute">
                {/* Arcs extérieurs des sous-scores */}
                {subScores.map((val, i) => {
                    const s = Math.min(100, Math.max(0, val ?? 0));
                    const startDeg = i * (360 / N) + segGap / 2;
                    const endBg = startDeg + segSpan;
                    // largeArc=0 toujours : max segSpan ≈ 67° < 180°
                    const p1 = pt(startDeg);
                    const pBg = pt(endBg);
                    const bgPath = `M ${p1.x} ${p1.y} A ${subRadius} ${subRadius} 0 0 1 ${pBg.x} ${pBg.y}`;

                    const endFill = startDeg + segSpan * s / 100;
                    const pFill = pt(endFill);
                    const fillPath = s > 1
                        ? `M ${p1.x} ${p1.y} A ${subRadius} ${subRadius} 0 0 1 ${pFill.x} ${pFill.y}`
                        : null;

                    const arcColor = colors[i] ?? '#94a3b8';
                    const isHovered = hoveredIndex === i;
                    const strokeW = isHovered ? subStroke + 6 : subStroke;

                    return (
                        <g
                            key={i}
                            onMouseEnter={() => onArcHover?.(i)}
                            onMouseLeave={() => onArcHover?.(null)}
                            style={{ cursor: onArcHover ? 'pointer' : 'default' }}
                        >
                            <path d={bgPath} fill="none" stroke="#e5e7eb" strokeWidth={subStroke} strokeLinecap="round" />
                            {fillPath && (
                                <path
                                    d={fillPath}
                                    fill="none"
                                    stroke={arcColor}
                                    strokeWidth={strokeW}
                                    strokeLinecap="round"
                                    style={{
                                        transition: 'stroke 0.2s ease, stroke-width 0.2s ease',
                                        filter: isHovered ? `drop-shadow(0 2px 8px ${arcColor}50)` : undefined,
                                    }}
                                />
                            )}
                            {/* Zone de survol élargie (invisible) */}
                            <path d={bgPath} fill="none" stroke="transparent" strokeWidth={subStroke + 16} strokeLinecap="round" />
                        </g>
                    );
                })}
                {/* Anneau principal */}
                <g transform={`rotate(-90 ${cx} ${cy})`}>
                    <circle cx={cx} cy={cy} r={mainRadius} fill="none" stroke="#f1f5f9" strokeWidth={mainStroke} />
                    <circle
                        cx={cx} cy={cy} r={mainRadius} fill="none" stroke={mainColor}
                        strokeWidth={mainStroke} strokeLinecap="round"
                        strokeDasharray={mainCirc} strokeDashoffset={mainOffset}
                        style={{ transition: 'stroke-dashoffset 1.1s cubic-bezier(0.22, 1, 0.36, 1)' }}
                    />
                </g>
            </svg>
            <div className="flex flex-col items-center z-10">
                <span className="font-extrabold text-gray-900" style={{ fontSize: size * 0.22, lineHeight: 1.1 }}>{score}</span>
                <span className="text-gray-400 font-medium" style={{ fontSize: size * 0.065 }}>/100</span>
            </div>
            {/* Tooltip au survol — style glass, pointer-events-none pour ne pas bloquer le badge en dessous */}
            {hoveredIndex != null && subLabels?.[hoveredIndex] && (
                <div
                    className="absolute left-1/2 -translate-x-1/2 w-[calc(100%+24px)] max-w-[200px] px-3 py-2 rounded-xl text-center z-20 pointer-events-none"
                    style={{
                        bottom: -6,
                        background: 'rgba(255,255,255,0.75)',
                        backdropFilter: 'blur(12px)',
                        WebkitBackdropFilter: 'blur(12px)',
                        border: '1px solid rgba(255,255,255,0.8)',
                        boxShadow: '0 4px 20px rgba(31,38,135,0.06)',
                    }}
                >
                    <div className="text-xs font-semibold text-gray-800 leading-snug">
                        {subLabels[hoveredIndex]}
                    </div>
                    <div className="text-xs text-neutral-500 font-medium mt-1">
                        {Math.round(subScores[hoveredIndex] ?? 0)}/100
                    </div>
                </div>
            )}
        </div>
    );
}

function StatBadge({ icon: Icon, label, value, ok, neutral }) {
    const color = neutral ? '#1e40af' : ok ? '#16a34a' : '#ef4444';
    const bg = neutral ? 'rgba(99,102,241,0.08)' : ok ? 'rgba(22,163,74,0.08)' : 'rgba(239,68,68,0.08)';
    const border = neutral ? 'rgba(99,102,241,0.2)' : ok ? 'rgba(22,163,74,0.2)' : 'rgba(239,68,68,0.2)';
    return (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl border" style={{ background: bg, borderColor: border }}>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: neutral ? 'rgba(99,102,241,0.12)' : ok ? 'rgba(22,163,74,0.12)' : 'rgba(239,68,68,0.12)' }}>
                <Icon size={16} style={{ color }} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-xs text-gray-400 font-medium">{label}</div>
                <div className="text-sm font-bold break-words" style={{ color }}>{value}</div>
            </div>
            {!neutral && (ok
                ? <CheckCircle size={16} className="text-green-500 flex-shrink-0" />
                : <XCircle size={16} className="text-red-400 flex-shrink-0" />
            )}
        </div>
    );
}

function GooglePreviewCard({ audit }) {
    return (
        <GoogleSerpPreview
            title={audit?.title}
            url={audit?.url}
            metaDescription={audit?.meta_description}
            scale={0.95}
            className="rounded-xl border border-neutral-200/60 bg-neutral-50/80 p-3"
        />
    );
}

function PerformanceMetricCards({ audit }) {
    const items = [
        { label: 'Desktop', value: audit?.pagespeed_performance_desktop, icon: Monitor },
        { label: 'Mobile', value: audit?.pagespeed_performance_mobile, icon: Smartphone },
        { label: 'Accessibilité', value: audit?.pagespeed_accessibility, icon: Eye },
        { label: 'Bonnes pratiques', value: audit?.pagespeed_best_practices, icon: Shield },
        { label: 'SEO (PS)', value: audit?.pagespeed_seo, icon: Search },
    ];
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
            {items.map(({ label, value, icon: Icon }) => {
                const raw = value ?? 0;
                const v = Math.round(Number(raw));
                const color = v >= 90 ? 'text-green-600' : v >= 50 ? 'text-amber-600' : 'text-red-600';
                return (
                    <div key={label} className="flex items-center gap-3 p-3 rounded-xl bg-neutral-50 border border-neutral-200/60 min-h-[52px]">
                        <div className="p-2 rounded-lg bg-accent/10 shrink-0">
                            <Icon className="text-accent" size={16} />
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className={`text-base font-bold leading-tight ${color}`}>
                                {v}/100
                            </div>
                            <div className="text-sm text-neutral-500 font-medium">{label}</div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function TechniqueInfoCards({ audit }) {
    const ti = audit?.technical_info || {};
    const items = [
        { label: 'CMS', value: ti.cms, icon: Code },
        { label: 'CDN', value: ti.cdn, icon: Globe },
        { label: 'Outils marketing', value: ti.marketing_tools, icon: BarChart3 },
    ];
    if (ti.is_multilingual) {
        items.push({ label: 'Site multilingue', value: 'Oui', sub: ti.hreflang_languages, icon: Languages });
    }
    const visible = items.filter((i) => i.value || i.sub);
    if (visible.length === 0) return null;
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
            {visible.map(({ label, value, sub, icon: Icon }) => (
                <div key={label} className="flex items-center gap-3 p-3 rounded-xl bg-neutral-50 border border-neutral-200/60 min-h-[52px]">
                    <div className="p-2 rounded-lg shrink-0" style={{ background: 'rgba(99,102,241,0.1)' }}>
                        <Icon size={16} className="text-indigo-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="text-xs font-medium text-neutral-500 uppercase tracking-wide">{label}</div>
                        <div className="text-sm font-bold text-gray-900">{value || '—'}</div>
                        {sub && <div className="text-xs text-neutral-500">{sub}</div>}
                    </div>
                </div>
            ))}
        </div>
    );
}

function TechItem({ icon: Icon, label, value, sub }) {
    if (!value && !sub) return null;
    return (
        <div className="flex items-center gap-3 p-4 rounded-xl backdrop-blur-sm bg-white/50 border border-white/60 min-h-[80px] shadow-sm">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(99,102,241,0.1)' }}>
                <Icon size={20} className="text-indigo-600" />
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-xs font-medium text-neutral-500 uppercase tracking-wide">{label}</div>
                <div className="font-semibold text-gray-900 mt-0.5 truncate">{value || <span className="text-neutral-400 italic font-normal">Non renseigné</span>}</div>
                {sub && <div className="text-xs text-neutral-500 mt-1">{sub}</div>}
            </div>
        </div>
    );
}

/**
 * En-tête d'onglet : chiffre (jauge) à gauche, explication vulgarisée à droite.
 * @param {Object} props
 * @param {number} props.score - Score sur 100
 * @param {string} props.title - Titre du score
 * @param {string} props.badge - Libellé du badge (ex. "Bien optimisé")
 * @param {string} props.explanation - Explication vulgarisée de ce qu'on analyse et pourquoi c'est important
 * @param {string} props.variant - 'good' | 'warning' | 'bad'
 * @param {React.ReactNode} props.belowInterpretation - Contenu à afficher sous l'interprétation
 */
function TabScoreHeader({ score, title, badge, explanation, subtitle, variant = 'info', gaugeSize = 120, cardStyle, interpretation, belowInterpretation }) {
    const [helpModalOpen, setHelpModalOpen] = useState(false);
    const badgeStyle = variant === 'good'
        ? { background: '#dcfce7', color: '#16a34a' }
        : variant === 'warning'
            ? { background: '#fef3c7', color: '#b45309' }
            : { background: '#f5f5f4', color: '#78716c' };
    const interpretationStyles = {
        good: 'bg-green-50 text-green-800 border-green-200',
        warning: 'bg-amber-50/80 text-amber-900 border-amber-200',
        bad: 'bg-stone-50 text-stone-700 border-stone-200',
        info: 'bg-amber-50/60 text-stone-800 border-amber-200/60',
    };
    const interpretationStyle = interpretationStyles[variant] || interpretationStyles.info;
    return (
        <>
            <div className="rounded-2xl p-5 md:p-8 shadow-xl border relative" style={cardStyle}>
                {explanation && (
                    <button
                        type="button"
                        onClick={() => setHelpModalOpen(true)}
                        className="absolute right-3 top-3 md:right-4 md:top-4 p-2 rounded-full text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 transition-colors"
                        aria-label="Pourquoi c'est important"
                    >
                        <HelpCircle size={18} />
                    </button>
                )}
                {/* Mobile : jauge puis bloc texte puis belowInterpretation en colonne. Desktop : ligne (jauge | contenu) */}
                <div className="flex flex-col md:flex-row md:items-center gap-5 md:gap-8">
                    <div className="flex-shrink-0 flex flex-col items-center">
                        <ScoreGauge value={score} size={gaugeSize} />
                        <span className="mt-2 md:mt-3 inline-block px-3 py-1 md:px-4 md:py-2 rounded-full text-xs md:text-sm font-bold" style={badgeStyle}>
                            {badge}
                        </span>
                    </div>
                    <div className="flex-1 text-left min-w-0 space-y-2 md:space-y-3 w-full">
                        <div>
                            <h3 className="text-base md:text-lg font-bold text-gray-900">{title}</h3>
                            {subtitle && <p className="text-xs md:text-sm text-neutral-500 mt-1 leading-relaxed break-words">{subtitle}</p>}
                        </div>
                        {interpretation && (
                            <div className={`text-xs md:text-sm p-3 md:p-4 rounded-xl border font-medium leading-relaxed ${interpretationStyle}`}>
                                {interpretation}
                            </div>
                        )}
                        {belowInterpretation && (
                            <div className="w-full">
                                {belowInterpretation}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {explanation && helpModalOpen && createPortal(
                <div className="fixed inset-0 z-[10000]" style={{ isolation: 'isolate' }}>
                    <div className="absolute inset-0 bg-black/50 backdrop-blur-md" aria-hidden onClick={() => setHelpModalOpen(false)} />
                    <div className="absolute left-1/2 top-1/2 w-full max-w-md -translate-x-1/2 -translate-y-1/2 px-4" role="dialog" aria-modal="true" aria-labelledby="help-modal-title">
                        <div className="rounded-2xl shadow-2xl border border-white/80 bg-white/95 backdrop-blur-md p-6 relative" style={{ boxShadow: '0 25px 50px -12px rgba(0,0,0,0.2)' }} onClick={(e) => e.stopPropagation()}>
                            <button type="button" onClick={() => setHelpModalOpen(false)} className="absolute right-3 top-3 p-1.5 rounded-lg text-gray-400 hover:text-neutral-600 hover:bg-gray-100 transition-colors" aria-label="Fermer">
                                <X size={20} />
                            </button>
                            <h2 id="help-modal-title" className="text-lg font-bold text-gray-900 pr-8 mb-3">Pourquoi c'est important</h2>
                            <div className="text-neutral-600 text-sm leading-relaxed">{explanation}</div>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
}

/**
 * En-tête d'onglet avec info à gauche (pas de score) — pour Analyse IA.
 * Même logique que TabScoreHeader : interpretation dans un bloc, belowInterpretation en dessous, explanation en collapsible.
 */
function TabInfoHeader({ leftContent, title, explanation, cardStyle, interpretation, belowInterpretation }) {
    const [helpModalOpen, setHelpModalOpen] = useState(false);
    return (
        <>
            <div className="rounded-2xl p-5 md:p-8 shadow-xl border mb-6 relative" style={cardStyle}>
                {explanation && (
                    <button
                        type="button"
                        onClick={() => setHelpModalOpen(true)}
                        className="absolute right-3 top-3 md:right-4 md:top-4 p-2 rounded-full text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 transition-colors"
                        aria-label="Pourquoi c'est important"
                    >
                        <HelpCircle size={18} />
                    </button>
                )}
                <div className="flex flex-col md:flex-row md:items-center gap-5 md:gap-8">
                    <div className="flex-shrink-0 flex flex-col items-center justify-center">
                        {leftContent}
                    </div>
                    <div className="flex-1 text-left min-w-0 space-y-2 md:space-y-3 w-full">
                        <div>
                            <h3 className="text-base md:text-lg font-bold text-gray-900">{title}</h3>
                        </div>
                        {interpretation && (
                            <div className="text-xs md:text-sm p-3 md:p-4 rounded-xl border font-medium leading-relaxed bg-blue-50 text-blue-800 border-blue-200">
                                <strong>Ce que l'IA a compris de votre site :</strong> {interpretation}
                            </div>
                        )}
                        {belowInterpretation && (
                            <div className="w-full">
                                {belowInterpretation}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {explanation && helpModalOpen && createPortal(
                <div className="fixed inset-0 z-[10000]" style={{ isolation: 'isolate' }}>
                    <div className="absolute inset-0 bg-black/50 backdrop-blur-md" aria-hidden onClick={() => setHelpModalOpen(false)} />
                    <div className="absolute left-1/2 top-1/2 w-full max-w-md -translate-x-1/2 -translate-y-1/2 px-4" role="dialog" aria-modal="true" aria-labelledby="help-modal-title-ia">
                        <div className="rounded-2xl shadow-2xl border border-white/80 bg-white/95 backdrop-blur-md p-6 relative" style={{ boxShadow: '0 25px 50px -12px rgba(0,0,0,0.2)' }} onClick={(e) => e.stopPropagation()}>
                            <button type="button" onClick={() => setHelpModalOpen(false)} className="absolute right-3 top-3 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors" aria-label="Fermer">
                                <X size={20} />
                            </button>
                            <h2 id="help-modal-title-ia" className="text-lg font-bold text-gray-900 pr-8 mb-3">Pourquoi c'est important</h2>
                            <div className="text-neutral-600 text-sm leading-relaxed">{explanation}</div>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
}

const TABS = [
    { key: 'synthese', label: 'Synthèse', mobileLabel: 'Synthèse' },
    { key: 'technique', label: 'Technique', mobileLabel: 'Technique' },
    { key: 'marche', label: 'Visibilité Google', mobileLabel: 'Marché', locked: true },
    { key: 'seo', label: 'SEO', mobileLabel: 'SEO' },
    { key: 'performance', label: 'Performance', mobileLabel: 'Perf.' },
    { key: 'visuel', label: 'Audit visuel', mobileLabel: 'Visuel' },
    { key: 'ia', label: 'Analyse IA', mobileLabel: 'IA' },
];

function getOrCreateSessionId() {
    let sid = sessionStorage.getItem('pub_session_id');
    if (!sid) {
        sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
        sessionStorage.setItem('pub_session_id', sid);
    }
    return sid;
}

function isReturnVisitor(token) {
    const lsKey = `pub_visited_${token}`;
    const ssKey = `pub_return_counted_${token}`;
    const hasVisitedBefore = !!localStorage.getItem(lsKey);
    const alreadyCountedThisSession = !!sessionStorage.getItem(ssKey);
    // Marquer : a visité (localStorage) + retour déjà compté dans cette session (sessionStorage)
    localStorage.setItem(lsKey, Date.now().toString());
    sessionStorage.setItem(ssKey, '1');
    // Vrai retour = déjà venu un jour + pas encore compté comme retour dans cette session
    return hasVisitedBefore && !alreadyCountedThisSession;
}

// Timer qui ne comptabilise que le temps où l'onglet est visible (Page Visibility API).
const _activeTimer = {
    activeMs: 0,
    // null quand l'onglet est en arrière-plan
    lastVisibleStart: document.visibilityState === 'visible' ? Date.now() : null,
};

function _getActiveSeconds() {
    const extraMs = _activeTimer.lastVisibleStart != null
        ? Date.now() - _activeTimer.lastVisibleStart
        : 0;
    return (_activeTimer.activeMs + extraMs) / 1000;
}

function _onPageVisibilityChange() {
    if (document.visibilityState === 'visible') {
        // Onglet redevient actif : relancer le chrono
        _activeTimer.lastVisibleStart = Date.now();
    } else {
        // Onglet mis en arrière-plan : accumuler et geler
        if (_activeTimer.lastVisibleStart != null) {
            _activeTimer.activeMs += Date.now() - _activeTimer.lastVisibleStart;
            _activeTimer.lastVisibleStart = null;
        }
    }
}

let _lastTabSwitchTime = Date.now();
let _currentTab = 'synthese';

/** Visite staff = UTM dans l’URL (ex. lien depuis l’admin Prospects). On ne track pas ces sessions. */
function isStaffAuditVisit() {
    if (typeof window === 'undefined' || !window.location?.search) return false;
    const params = new URLSearchParams(window.location.search);
    return params.get('utm_source') === 'staff' || params.get('utm_medium') === 'staff';
}

async function trackEvent({ token, eventType, eventLabel, page = 'result', duration, extraData }) {
    if (isStaffAuditVisit()) return;
    try {
        await fetch(`${API_BASE_URL}/api/public/event`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                token: token || null,
                event_type: eventType,
                event_label: eventLabel,
                page,
                session_id: getOrCreateSessionId(),
                duration: duration || null,
                extra_data: extraData ? JSON.stringify(extraData) : null,
                user_agent: navigator.userAgent,
            }),
        });
    } catch (_) { /* silently ignore */ }
}

function sendBeaconEvent({ token, eventType, eventLabel, page = 'result', duration, extraData }) {
    if (isStaffAuditVisit()) return;
    try {
        const payload = JSON.stringify([{
            token: token || null,
            event_type: eventType,
            event_label: eventLabel,
            page,
            session_id: getOrCreateSessionId(),
            duration: duration || null,
            extra_data: extraData ? JSON.stringify(extraData) : null,
            user_agent: navigator.userAgent,
        }]);
        const url = `${API_BASE_URL}/api/public/event/batch`;
        // fetch keepalive évite le CORS credentials:include imposé par sendBeacon cross-origin.
        if (typeof fetch !== 'undefined') {
            fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: payload, keepalive: true, credentials: 'omit' }).catch(() => {});
        } else if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
            navigator.sendBeacon(url, new Blob([payload], { type: 'application/json' }));
        }
    } catch (_) { /* ignore */ }
}

// ── Loading steps page (même structure visuelle que l'étape 2 de la landing) ─

const LOADING_STEPS = [
    { icon: Search, label: 'Analyse SEO & balises', desc: 'Titre, meta, H1, images…' },
    { icon: Gauge, label: 'Performance & Core Web Vitals', desc: 'Vitesse desktop & mobile' },
    { icon: Wrench, label: 'Fondations techniques', desc: 'HTTPS, indexation, mobile…' },
    { icon: Globe, label: 'Visibilité sur Google', desc: 'Trafic organique, mots-clés' },
    { icon: Sparkles, label: 'Analyse IA du contenu', desc: 'Positionnement & message' },
];

const POLL_INTERVAL_MS = 2000;
const LONG_WAIT_MS = 90000; // 90 s → afficher message de secours
const AUDIT_LOADING_START_KEY = (t) => `audit_loading_start_${t}`;
const STEP_DURATION_MS = 6000; // une étape visuelle toutes les 6 s (après refresh on reprend où on en était)

function AuditLoadingSteps({ token, auditUrl, initialStepsCompleted = 0, onDone, onError }) {
    const [completedSteps, setCompletedSteps] = useState(() => {
        try {
            let start = sessionStorage.getItem(AUDIT_LOADING_START_KEY(token));
            if (!start) {
                start = String(Date.now());
                sessionStorage.setItem(AUDIT_LOADING_START_KEY(token), start);
            }
            const elapsed = Date.now() - parseInt(start, 10);
            const fromElapsed = Math.min(LOADING_STEPS.length, Math.floor(elapsed / STEP_DURATION_MS));
            return Math.min(Math.max(0, fromElapsed, initialStepsCompleted), LOADING_STEPS.length);
        } catch {
            return Math.min(Math.max(0, initialStepsCompleted), LOADING_STEPS.length);
        }
    });
    const [longWait, setLongWait] = useState(false);
    const pollIntervalRef = React.useRef(null);
    const stepIntervalRef = React.useRef(null);
    const longWaitRef = React.useRef(null);
    const onDoneRef = React.useRef(onDone);
    const onErrorRef = React.useRef(onError);
    onDoneRef.current = onDone;
    onErrorRef.current = onError;

    useEffect(() => {
        longWaitRef.current = setTimeout(() => setLongWait(true), LONG_WAIT_MS);
        return () => {
            if (longWaitRef.current) clearTimeout(longWaitRef.current);
        };
    }, []);

    useEffect(() => {
        async function poll() {
            try {
                const res = await fetch(`${API_BASE_URL}/api/public/audit/${token}?_t=${Date.now()}`, { cache: 'no-store' });
                if (!res.ok) return;
                const data = await res.json();
                if (data.status === 'done') {
                    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
                    if (longWaitRef.current) clearTimeout(longWaitRef.current);
                    pollIntervalRef.current = null;
                    try { sessionStorage.removeItem(AUDIT_LOADING_START_KEY(token)); } catch (_) {}
                    onDoneRef.current(data);
                    return;
                }
                if (data.status === 'error') {
                    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
                    if (longWaitRef.current) clearTimeout(longWaitRef.current);
                    pollIntervalRef.current = null;
                    try { sessionStorage.removeItem(AUDIT_LOADING_START_KEY(token)); } catch (_) {}
                    onErrorRef.current(data.error || "L'audit a échoué.");
                    return;
                }
            } catch {
                // ignore network errors, keep polling
            }
        }

        poll();
        pollIntervalRef.current = setInterval(poll, 2000);
        return () => {
            if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
        };
    }, [token]);

    // Avancement visuel des étapes (comme le teaser de l'étape 2)
    useEffect(() => {
        stepIntervalRef.current = setInterval(() => {
            setCompletedSteps((s) => Math.min(s + 1, LOADING_STEPS.length));
        }, 5000);
        return () => {
            if (stepIntervalRef.current) clearInterval(stepIntervalRef.current);
        };
    }, []);

    const displayUrl = auditUrl ? auditUrl.replace(/^https?:\/\//, '').replace(/\/$/, '') : '';

    return (
        <div className="min-h-screen flex flex-col items-center justify-center px-4 py-8 sm:py-12 relative overflow-hidden">
            <div className="relative z-10 w-full max-w-md">
                <h1 className="text-center text-lg sm:text-xl md:text-2xl font-semibold text-neutral-800 mb-4 sm:mb-6 px-1">
                    Veuillez patienter, nous effectuons votre audit
                </h1>
                {/* Même structure que le teaser étape 2 : header + étapes + aperçu score */}
                <div className="flex flex-col gap-4">
                    {/* Header */}
                    <div className="flex items-center gap-3 mb-1">
                        <div className="flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-300"
                            style={{ background: 'rgba(0,222,222,0.1)' }}>
                            <Search size={16} style={{ color: '#00a0a0' }} />
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="text-xs text-neutral-400 font-medium">Analyse en cours pour</div>
                            <div className="text-sm font-bold text-neutral-900 truncate">{displayUrl || 'votre site'}</div>
                        </div>
                        <div className="flex-shrink-0">
                            <span className="text-xs font-semibold px-2.5 py-1 rounded-full text-neutral-500 bg-neutral-100 animate-pulse">
                                En cours…
                            </span>
                        </div>
                    </div>

                    {/* Steps (même rendu que AuditTeaser) */}
                    <div className="space-y-2 sm:space-y-2.5">
                        {LOADING_STEPS.map(({ icon: Icon, label, desc }, i) => {
                            const isDoneStep = i < completedSteps;
                            const isActiveStep = i === completedSteps && completedSteps < LOADING_STEPS.length;

                            return (
                                <div
                                    key={label}
                                    className={`flex items-center gap-2.5 sm:gap-3 p-2.5 sm:p-3 rounded-xl border transition-all duration-500 ${
                                        isDoneStep
                                            ? 'bg-emerald-50/80 border-emerald-200/60'
                                            : isActiveStep
                                                ? 'bg-[#00dede]/5 border-[#00dede]/30'
                                                : 'bg-neutral-50/60 border-neutral-200/40 opacity-35'
                                    }`}
                                >
                                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                        isDoneStep ? 'bg-emerald-100'
                                            : isActiveStep ? 'bg-[#00dede]/10'
                                                : 'bg-neutral-100'
                                    }`}>
                                        {isDoneStep
                                            ? <CheckCircle size={14} className="text-emerald-500" />
                                            : isActiveStep
                                                ? <div className="w-3 h-3 border-2 rounded-full border-[#00a0a0]/30 border-t-[#00a0a0] animate-spin" />
                                                : <Icon size={14} className="text-neutral-400" />
                                        }
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className={`text-xs font-semibold leading-tight ${
                                            isDoneStep ? 'text-emerald-700' : isActiveStep ? 'text-neutral-900' : 'text-neutral-500'
                                        }`}>
                                            {label}
                                        </div>
                                        <div className="text-[11px] mt-0.5 truncate text-neutral-400">{desc}</div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Aperçu score flou + bloc "Préparation du rapport" — plus rassurant et lisible */}
                    <div className="rounded-xl border border-neutral-200/60 bg-neutral-50/50 p-3 sm:p-4 relative overflow-hidden">
                        <div className="blur-sm select-none pointer-events-none">
                            <div className="flex items-center gap-4">
                                <div className="relative flex-shrink-0">
                                    <svg width="68" height="68" viewBox="0 0 68 68">
                                        <circle cx="34" cy="34" r="26" fill="none" stroke="#f1f5f9" strokeWidth="7" />
                                        <circle cx="34" cy="34" r="26" fill="none" stroke="#00dede" strokeWidth="7"
                                            strokeDasharray="163" strokeDashoffset="49" strokeLinecap="round"
                                            transform="rotate(-90 34 34)" />
                                    </svg>
                                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                                        <span className="text-base font-extrabold text-neutral-900">71</span>
                                        <span className="text-[9px] text-neutral-400">/100</span>
                                    </div>
                                </div>
                                <div className="flex-1 space-y-2">
                                    {['Référencement', 'Performance', 'Technique'].map((l, j) => (
                                        <div key={l} className="flex items-center gap-2">
                                            <span className="text-[10px] text-neutral-500 w-20 truncate">{l}</span>
                                            <div className="flex-1 h-1.5 rounded-full bg-neutral-200 overflow-hidden">
                                                <div className="h-full rounded-full" style={{ width: `${[80, 65, 90][j]}%`, background: ['#5b8fd4', '#5eb89a', '#e5a855'][j] }} />
                                            </div>
                                            <span className="text-[10px] font-bold text-neutral-600">{[80, 65, 90][j]}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        {/* Bloc préparation — message rassurant + barre de progression indéterminée */}
                        <div className="absolute inset-0 flex flex-col items-center justify-center rounded-xl bg-white/90 backdrop-blur-sm border border-[#00dede]/20 px-4 py-5">
                            <div className="w-full max-w-xs text-center space-y-3">
                                <p className="text-sm font-semibold text-neutral-900">On prépare votre rapport</p>
                                <p className="text-xs text-neutral-600">Quelques secondes encore…</p>
                                <div className="w-full h-1.5 rounded-full bg-neutral-100 overflow-hidden" role="progressbar" aria-valuetext="En cours">
                                    <div className="loading-bar-slide h-full w-1/3 min-w-[60px] rounded-full bg-[#00dede]" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <p className="text-center text-xs text-neutral-400">
                        Restez sur cette page, votre rapport personnalisé arrive.
                    </p>
                    {longWait && (
                        <p className="text-center text-xs text-amber-600 mt-3">
                            Cela prend plus de temps que prévu.{" "}
                            <button
                                type="button"
                                onClick={() => window.location.reload()}
                                className="underline font-medium hover:no-underline"
                            >
                                Rafraîchir la page
                            </button>
                            {" "}pour vérifier si votre rapport est prêt.
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}

// ── Main component ──────────────────────────────────────────────────

export default function PublicAuditResult() {
    const { token } = useParams();
    const location = useLocation();
    const prefetched = location.state?.prefetchedAudit;
    const trackingSearch = getTrackingSearch(location.search) || getPersistedTrackingSearch();
    const [audit, setAudit] = useState(prefetched ?? null);
    const [loading, setLoading] = useState(!prefetched);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('synthese');
    const [ctaLink, setCtaLink] = useState(DEFAULT_CTA_LINK);
    const [iaData, setIaData] = useState(prefetched?.ia_data ?? null);
    const [openCriterion, setOpenCriterion] = useState(null);
    const [syntheseHoveredIndex, setSyntheseHoveredIndex] = useState(null);
    const [iaExplanationOpen, setIaExplanationOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [isFirstVisit, setIsFirstVisit] = useState(false);
    const [onboardingStep, setOnboardingStep] = useState(0);
    const [visitedTabKeys, setVisitedTabKeys] = useState(() => ['synthese']);
    const [showConversionModal, setShowConversionModal] = useState(false);
    const [conversionModalStep, setConversionModalStep] = useState('intro'); // 'intro' | 'form' | 'success'
    const [callbackMessage, setCallbackMessage] = useState('');
    const [callbackSlot, setCallbackSlot] = useState('indifferent');
    const [callbackSubmitLoading, setCallbackSubmitLoading] = useState(false);
    const [callbackError, setCallbackError] = useState('');
    const syntheseHeroRef = useRef(null);
    const onboardingNavRef = useRef(null);
    const conversionModalTimeoutRef = useRef(null);
    const [cardPosition, setCardPosition] = useState(null);
    const arrowMarkerId = useId();

    const updateOnboardingPosition = useCallback(() => {
        const cardHalf = 192;
        const gap = 20;
        const padding = 16;
        if (onboardingStep === 1 && syntheseHeroRef.current) {
            const r = syntheseHeroRef.current.getBoundingClientRect();
            const left = Math.min(Math.max(cardHalf + padding, r.left + r.width / 2), window.innerWidth - cardHalf - padding);
            setCardPosition({ top: r.bottom + gap, left });
        } else if (onboardingStep === 2 && onboardingNavRef.current) {
            const r = onboardingNavRef.current.getBoundingClientRect();
            const left = Math.min(Math.max(cardHalf + padding, r.left + r.width / 2), window.innerWidth - cardHalf - padding);
            setCardPosition({ top: r.bottom + gap, left });
        } else {
            setCardPosition(null);
        }
    }, [onboardingStep]);

    useEffect(() => {
        if (onboardingStep < 1) {
            setCardPosition(null);
            return;
        }
        const measure = () => requestAnimationFrame(updateOnboardingPosition);
        measure();
        const t = setTimeout(updateOnboardingPosition, 80);
        window.addEventListener('scroll', measure, true);
        window.addEventListener('resize', measure);
        return () => {
            clearTimeout(t);
            window.removeEventListener('scroll', measure, true);
            window.removeEventListener('resize', measure);
        };
    }, [onboardingStep, updateOnboardingPosition, audit]);

    // Favicon : audit gratuit → favicon.svg, reste de l’app → favicon.ico
    useEffect(() => {
        const link = document.querySelector('link[rel="icon"]');
        if (!link) return;
        link.setAttribute('href', '/favicon.svg');
        link.setAttribute('type', 'image/svg+xml');
        return () => {
            link.setAttribute('href', '/favicon-metrics.svg');
            link.setAttribute('type', 'image/svg+xml');
        };
    }, []);

    // Title et meta pour la page résultat (audit gratuit)
    useEffect(() => {
        let title = "Résultats de votre audit SEO & GEO | Ascanio";
        const metaEl = document.querySelector('meta[name="description"]');
        if (audit?.url) {
            try {
                const domain = new URL(audit.url).hostname.replace(/^www\./, '');
                title = `Audit SEO & GEO - ${domain} | Ascanio`;
            } catch {
                title = "Résultats de votre audit SEO & GEO | Ascanio";
            }
        }
        document.title = title;
        if (metaEl) metaEl.setAttribute('content', PUBLIC_AUDIT_RESULT_META_DESC);
        const canonicalUrl = typeof window !== 'undefined' ? window.location.href : "https://audit.ascan.io/audit";
        const imageUrl = typeof window !== 'undefined'
            ? `${window.location.origin}/assets/logo-ascanio.png`
            : "https://audit.ascan.io/assets/logo-ascanio.png";
        upsertMetaTag('property', 'og:title', title);
        upsertMetaTag('property', 'og:description', PUBLIC_AUDIT_RESULT_META_DESC);
        upsertMetaTag('property', 'og:type', 'website');
        upsertMetaTag('property', 'og:url', canonicalUrl);
        upsertMetaTag('property', 'og:image', imageUrl);
        upsertMetaTag('name', 'twitter:card', 'summary_large_image');
        upsertMetaTag('name', 'twitter:title', title);
        upsertMetaTag('name', 'twitter:description', PUBLIC_AUDIT_RESULT_META_DESC);
        upsertMetaTag('name', 'twitter:image', imageUrl);
        return () => {
            document.title = "Metrics";
            if (metaEl) metaEl.setAttribute('content', "Application web d'analyse SEO avec intégration Google Search Console");
            upsertMetaTag('property', 'og:title', 'Metrics');
            upsertMetaTag('property', 'og:description', "Application web d'analyse SEO avec intégration Google Search Console");
            upsertMetaTag('property', 'og:type', 'website');
            upsertMetaTag('property', 'og:url', typeof window !== 'undefined' ? window.location.origin : "https://ascan.io");
            upsertMetaTag('property', 'og:image', typeof window !== 'undefined' ? `${window.location.origin}/favicon-metrics.svg` : "https://ascan.io/favicon-metrics.svg");
            upsertMetaTag('name', 'twitter:card', 'summary_large_image');
            upsertMetaTag('name', 'twitter:title', 'Metrics');
            upsertMetaTag('name', 'twitter:description', "Application web d'analyse SEO avec intégration Google Search Console");
            upsertMetaTag('name', 'twitter:image', typeof window !== 'undefined' ? `${window.location.origin}/favicon-metrics.svg` : "https://ascan.io/favicon-metrics.svg");
        };
    }, [audit?.url]);

    useEffect(() => {
        persistTrackingSearch(trackingSearch);
    }, [trackingSearch]);

    useEffect(() => {
        let cancelled = false;
        const fetchCtaConfig = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/api/public/cta-config?_t=${Date.now()}`, { cache: 'no-store' });
                if (!res.ok) return;
                const data = await res.json();
                const nextUrl = (data?.audit_cta_url || '').trim();
                if (!cancelled && nextUrl) setCtaLink(nextUrl);
            } catch (_) {
                // Fallback sur DEFAULT_CTA_LINK
            }
        };
        fetchCtaConfig();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        const onScroll = () => setIsScrolled(window.scrollY > 20);
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    // Scroll l'onglet actif dans la vue sur mobile (slider)
    useEffect(() => {
        const returnVisitor = isReturnVisitor(token);
        trackEvent({ token, eventType: 'page_view', eventLabel: 'result_page_view', extraData: {
            referrer: document.referrer || null,
            screen: `${window.innerWidth}x${window.innerHeight}`,
        }});
        if (returnVisitor) {
            trackEvent({ token, eventType: 'return_visit', eventLabel: 'return_visit' });
        }

        document.addEventListener('visibilitychange', _onPageVisibilityChange);

        const handleBeforeUnload = () => {
            const sessionDuration = _getActiveSeconds();
            const tabDuration = (Date.now() - _lastTabSwitchTime) / 1000;
            sendBeaconEvent({ token, eventType: 'session_end', eventLabel: 'session_end', duration: sessionDuration, extraData: {
                last_tab: _currentTab,
                last_tab_duration: Math.round(tabDuration),
            }});
        };
        window.addEventListener('beforeunload', handleBeforeUnload);

        // Scroll depth tracking désactivé (événements "Défilement XX %" retirés des stats)

        return () => {
            document.removeEventListener('visibilitychange', _onPageVisibilityChange);
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [token]);

    const hasVisualForNav = Boolean(
        (audit?.visual_score && Number(audit.visual_score) > 0) ||
        (audit?.visual_audit_data?.analysis?.score_visuel && Number(audit.visual_audit_data.analysis.score_visuel) > 0)
    );
    const visibleTabs = hasVisualForNav ? TABS : TABS.filter((t) => t.key !== 'visuel');

    useEffect(() => {
        if (activeTab === 'visuel' && !hasVisualForNav) {
            setActiveTab('synthese');
        }
    }, [activeTab, hasVisualForNav]);

    const handleTabChange = (key) => {
        if (!visibleTabs.some((t) => t.key === key)) return;
        const tabDuration = (Date.now() - _lastTabSwitchTime) / 1000;
        trackEvent({ token, eventType: 'tab_click', eventLabel: `tab_${key}`, duration: tabDuration, extraData: { from_tab: _currentTab } });
        _lastTabSwitchTime = Date.now();
        _currentTab = key;
        setActiveTab(key);
        window.scrollTo({ top: 0, behavior: 'auto' });
        setVisitedTabKeys((prev) => {
            if (prev.includes(key)) return prev;
            const next = [...prev, key];
            if (next.length === visibleTabs.length && token && !localStorage.getItem(CONVERSION_MODAL_STORAGE_KEY(token))) {
                if (conversionModalTimeoutRef.current) clearTimeout(conversionModalTimeoutRef.current);
                conversionModalTimeoutRef.current = setTimeout(() => {
                    conversionModalTimeoutRef.current = null;
                    setShowConversionModal(true);
                }, 7000);
            }
            return next;
        });
    };

    const openConversionModal = useCallback(() => {
        setConversionModalStep('intro');
        setCallbackMessage('');
        setCallbackSlot('indifferent');
        setCallbackError('');
        setShowConversionModal(true);
    }, []);

    const closeConversionModal = (fromCta = false) => {
        if (token) localStorage.setItem(CONVERSION_MODAL_STORAGE_KEY(token), '1');
        setShowConversionModal(false);
        setConversionModalStep('intro');
        setCallbackError('');
        if (fromCta) trackEvent({ token, eventType: 'cta_click', eventLabel: 'conversion_modal_cta' });
    };

    const submitCallbackRequest = async () => {
        setCallbackError('');
        setCallbackSubmitLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/public/audit/${token}/request-callback`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ message: callbackMessage.trim() || null, preferred_slot: callbackSlot || null }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.detail || 'Erreur lors de l\'envoi');
            trackEvent({ token, eventType: 'cta_click', eventLabel: 'conversion_modal_callback_submit' });
            setConversionModalStep('success');
        } catch (err) {
            setCallbackError(err.message || 'Une erreur est survenue. Réessayez.');
        } finally {
            setCallbackSubmitLoading(false);
        }
    };

    useEffect(() => {
        return () => {
            if (conversionModalTimeoutRef.current) clearTimeout(conversionModalTimeoutRef.current);
        };
    }, []);

    useEffect(() => {
        async function fetchAudit() {
            try {
                const res = await fetch(`${API_BASE_URL}/api/public/audit/${token}?_t=${Date.now()}`, { cache: 'no-store' });
                if (!res.ok) {
                    const data = await res.json();
                    throw new Error(data.detail || 'Audit non trouvé');
                }
                const data = await res.json();
                setAudit(data);
                setIaData(data.ia_data || null);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        }
        // Audit déjà chargé depuis la landing (transition fluide) → pas de fetch
        if (token && !prefetched) fetchAudit();
    }, [token, prefetched]);

    // Onboarding désactivé : ne plus bloquer ni guider sur la page résultat.
    useEffect(() => {
        if (!audit || !token) return;
        setIsFirstVisit(false);
        setOnboardingStep(0);
    }, [audit, token]);

    const finishOnboarding = () => {
        if (token) localStorage.setItem(ONBOARDING_STORAGE_KEY(token), '1');
        setOnboardingStep(0);
        setIsFirstVisit(false);
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center px-4">
                <div className="text-center backdrop-blur-md bg-white/65 rounded-2xl p-8 border border-white/80 shadow-xl" style={{ boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.08)' }}>
                    <div className="w-16 h-16 border-4 border-t-blue-600 rounded-full animate-spin mx-auto mb-6" style={{ borderColor: '#dbeafe', borderTopColor: '#1e40af' }} />
                    <h2 className="text-xl font-bold text-gray-900 mb-2">Analyse en cours...</h2>
                    <p className="text-gray-500 text-sm">Nous analysons votre site, cela peut prendre 20-30 secondes.</p>
                </div>
            </div>
        );
    }

    if (error || !audit) {
        return (
            <div className="min-h-screen flex items-center justify-center px-4">
                <div className="text-center backdrop-blur-md bg-white/65 rounded-2xl p-8 border border-white/80 shadow-xl" style={{ boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.08)' }}>
                    <XCircle size={48} className="text-red-400 mx-auto mb-4" />
                    <h2 className="text-xl font-bold text-gray-900 mb-2">Audit non trouvé</h2>
                    <p className="text-gray-500">{error || 'Ce lien est invalide ou a expiré.'}</p>
                </div>
            </div>
        );
    }

    if (audit.status === 'error') {
        const is403 = audit.error && (String(audit.error).includes('403') || String(audit.error).includes('bloque'));
        const landingBasePath = typeof window !== 'undefined' && window.location.host === 'audit.ascan.io' ? '/' : '/audit-gratuit';
        const landingPath = `${landingBasePath}${trackingSearch}`;
        return (
            <div className="min-h-screen flex items-center justify-center px-4">
                <div className="text-center backdrop-blur-md bg-white/65 rounded-2xl p-8 border border-white/80 shadow-xl max-w-md" style={{ boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.08)' }}>
                    {is403 ? (
                        <Lock size={48} className="text-amber-500 mx-auto mb-4" aria-hidden />
                    ) : (
                        <XCircle size={48} className="text-red-400 mx-auto mb-4" aria-hidden />
                    )}
                    <h2 className="text-xl font-bold text-gray-900 mb-2">
                        {is403 ? 'Ce site bloque les analyses automatiques' : 'Échec de l\'audit'}
                    </h2>
                    <p className="text-gray-600 text-sm leading-relaxed mb-6">{audit.error || 'Une erreur est survenue lors de l\'analyse.'}</p>
                    <Link
                        to={landingPath}
                        className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm text-white cta-audit-gratuit hover:opacity-95 transition-opacity"
                    >
                        <ArrowRight className="rotate-180" size={16} />
                        Retour à l’accueil
                    </Link>
                </div>
            </div>
        );
    }

    if (audit.status === 'processing' || audit.status === 'pending') {
        return (
            <AuditLoadingSteps
                token={token}
                auditUrl={audit.url}
                onDone={(data) => {
                    setAudit(data);
                    setIaData(data.ia_data || null);
                }}
                onError={(msg) => setError(msg || 'L\'audit a échoué.')}
            />
        );
    }

    const scoreSeo = audit.score_global || 0;
    const perfDesktop = audit.pagespeed_performance_desktop || 0;
    const perfMobile = audit.pagespeed_performance_mobile || 0;
    const perfAccess = audit.pagespeed_accessibility || 0;
    const perfBest = audit.pagespeed_best_practices || 0;
    const perfSeoPs = audit.pagespeed_seo || 0;
    const scorePerformance = Math.round(
        perfDesktop * 0.30 + perfMobile * 0.30 + perfAccess * 0.15 + perfBest * 0.15 + perfSeoPs * 0.10
    );
    const techChecks = [audit.is_https, audit.has_viewport, !!audit.lang, !audit.noindex, audit.has_structured_data, audit.has_canonical, audit.has_llm_file];
    const scoreTechnique = Math.round((techChecks.filter(Boolean).length / 7) * 100);
    const traffic = audit.semrush_data?.organic_traffic ?? 0;
    const hasMarcheData = audit.semrush_data?.organic_traffic != null;
    const scoreMarche = traffic >= 20000 ? 95 : traffic >= 5000 ? 80 : traffic >= 1000 ? 65 : traffic >= 100 ? 45 : traffic > 0 ? 25 : 0;
    const visualData = audit.visual_audit_data || null;
    const scoreVisuel = audit.visual_score ? Math.round(audit.visual_score) : (visualData?.analysis?.score_visuel ? Math.round(visualData.analysis.score_visuel) : null);
    const hasVisuelData = scoreVisuel != null && scoreVisuel > 0;
    const storedPreviewSrc = visualData?.screenshot_b64
        ? `data:image/png;base64,${visualData.screenshot_b64}`
        : null;
    const scoreGlobal = (() => {
        let num = scoreSeo + scorePerformance + scoreTechnique;
        let den = 3;
        if (hasMarcheData) { num += scoreMarche * 0.5; den += 0.5; }
        if (hasVisuelData) { num += scoreVisuel * 0.7; den += 0.7; }
        return Math.round(num / den);
    })();
    const scoreLabel = scoreGlobal >= 90 ? 'Bon' : scoreGlobal >= 60 ? 'À améliorer' : 'Insuffisant';
    const scoreLabelStyle = scoreGlobal >= 90
        ? { background: '#dcfce7', color: '#16a34a' }
        : scoreGlobal >= 60
            ? { background: '#fef3c7', color: '#d97706' }
            : { background: '#fee2e2', color: '#ef4444' };

    const card = {
        background: 'rgba(255, 255, 255, 0.65)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        border: '1px solid rgba(255, 255, 255, 0.8)',
        boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.08), 0 2px 8px 0 rgba(0, 0, 0, 0.04)',
    };
    const domain = audit.url ? new URL(audit.url).hostname : '';
    const faviconUrl = domain ? `https://www.google.com/s2/favicons?domain=${domain}&sz=64` : null;
    const synthesePreviewSrc = hasVisuelData ? (storedPreviewSrc || (audit.url
        ? `https://image.thum.io/get/width/1200/noanimate/${encodeURIComponent(audit.url)}`
        : null)) : null;

    return (
        <div className="min-h-screen relative pt-[88px] md:pt-[60px] pb-12 px-4 md:px-6">
            {/* Onboarding — première visite */}
            {onboardingStep >= 1 && activeTab === 'synthese' && (
                <>
                    {/* Overlay */}
                    <div
                        className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-[2px] onboarding-overlay-in pointer-events-auto"
                        aria-hidden
                    />

                    {/* ── MOBILE : bottom sheet ───────────────────── */}
                    <div className="fixed bottom-0 left-0 right-0 z-[70] md:hidden onboarding-card-in">
                        <div
                            className="bg-white rounded-t-2xl p-5 pb-8"
                            style={{ boxShadow: '0 -8px 40px rgba(0,0,0,0.18)' }}
                        >
                            {/* Drag handle */}
                            <div className="w-10 h-1 rounded-full bg-neutral-200 mx-auto mb-5" />
                            <div key={onboardingStep} className="onboarding-step-in">
                                {onboardingStep === 1 && (
                                    <>
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className="w-9 h-9 rounded-full bg-neutral-100 flex items-center justify-center onboarding-sparkle border border-neutral-200/60 flex-shrink-0">
                                                <Sparkles size={17} className="text-neutral-600" />
                                            </div>
                                            <div>
                                                <div className="font-bold text-gray-900 text-base leading-tight">Votre audit est prêt !</div>
                                                <div className="text-xs text-neutral-400 mt-0.5">Étape 1 / 2</div>
                                            </div>
                                        </div>
                                        <p className="text-sm text-gray-600 mb-5 leading-relaxed">
                                            Faites défiler vers le haut pour voir votre <strong>score global</strong> et les 5 catégories analysées. Appuyez sur une catégorie pour en voir le détail.
                                        </p>
                                        <button
                                            type="button"
                                            onClick={() => setOnboardingStep(2)}
                                            className="cta-audit-gratuit w-full py-3.5 rounded-xl font-semibold text-white min-h-[48px]"
                                        >
                                            Suivant →
                                        </button>
                                    </>
                                )}
                                {onboardingStep === 2 && (
                                    <>
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className="w-9 h-9 rounded-full bg-neutral-100 flex items-center justify-center onboarding-sparkle border border-neutral-200/60 flex-shrink-0">
                                                <MousePointer size={17} className="text-neutral-600" />
                                            </div>
                                            <div>
                                                <div className="font-bold text-gray-900 text-base leading-tight">Explorez le détail</div>
                                                <div className="text-xs text-neutral-400 mt-0.5">Étape 2 / 2</div>
                                            </div>
                                        </div>
                                        <p className="text-sm text-gray-600 mb-5 leading-relaxed">
                                            Les <strong>onglets surlignés en haut</strong> vous donnent accès au détail SEO, Performance, Technique, Marché et Analyse IA.
                                        </p>
                                        <button
                                            type="button"
                                            onClick={finishOnboarding}
                                            className="cta-audit-gratuit w-full py-3.5 rounded-xl font-semibold text-white min-h-[48px]"
                                        >
                                            C'est parti !
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ── DESKTOP : étape 1 = fixed bas droite + flèche arc ; étape 2 = positionnée sous la nav ─── */}
                    <div
                        className={`hidden md:flex fixed z-[70] flex-col items-end w-full max-w-sm pointer-events-none onboarding-card-place transition-all duration-[0.45s] ease-out ${
                            onboardingStep === 1 ? 'right-6 bottom-6' : cardPosition ? '' : 'left-1/2 bottom-6 -translate-x-1/2'
                        }`}
                        style={
                            onboardingStep === 1
                                ? undefined
                                : cardPosition
                                    ? {
                                          top: cardPosition.top,
                                          left: cardPosition.left,
                                          transform: 'translateX(-50%)',
                                          transition: 'top 0.45s cubic-bezier(0.22, 1, 0.36, 1), left 0.45s cubic-bezier(0.22, 1, 0.36, 1)',
                                      }
                                    : undefined
                        }
                    >
                        {onboardingStep === 1 ? (
                            <div className="onboarding-arrow-arc-bias onboarding-arrow-arc-wiggle mb-1 pointer-events-none self-start" aria-hidden>
                                <svg width="64" height="64" viewBox="-8 -8 80 80" fill="none" className="text-white overflow-visible" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.25))' }} shapeRendering="geometricPrecision">
                                    <defs>
                                        <marker id={arrowMarkerId} markerWidth="7" markerHeight="7" refX="0" refY="3.5" orient="auto">
                                            <path d="M 0 0 L 7 3.5 L 0 7 Z" fill="currentColor" />
                                        </marker>
                                    </defs>
                                    {/* Arc plus fluide (Bezier cubique) + pointe en marker pour une jonction propre */}
                                    <path
                                        d="M 50 50 C 50 22 22 14 14 14"
                                        stroke="currentColor"
                                        strokeWidth="2.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        fill="none"
                                        markerEnd={`url(#${arrowMarkerId.replace(/:/g, '\\:')})`}
                                    />
                                </svg>
                            </div>
                        ) : (
                            <div className="onboarding-arrow-bounce mb-2 pointer-events-none" aria-hidden>
                                <ArrowUp size={28} strokeWidth={2.5} className="text-white drop-shadow-sm" />
                            </div>
                        )}
                        <div
                            className={`w-full max-w-sm rounded-2xl shadow-xl border border-white/80 backdrop-blur-md p-4 pointer-events-auto ${
                                onboardingStep === 1 ? 'onboarding-card-bias onboarding-card-pulse' : 'onboarding-card-in onboarding-card-pulse'
                            }`}
                            style={{
                                background: 'rgba(255, 255, 255, 0.9)',
                                boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.12), 0 2px 8px 0 rgba(0, 0, 0, 0.06), 0 0 0 1px rgba(255,255,255,0.8)',
                            }}
                        >
                            <div key={onboardingStep} className="onboarding-step-in">
                                {onboardingStep === 1 && (
                                    <>
                                        <div className="flex items-center gap-2 mb-3">
                                            <div className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center onboarding-sparkle border border-neutral-200/60">
                                                <Sparkles size={16} className="text-neutral-600" />
                                            </div>
                                            <span className="font-semibold text-gray-800">Bienvenue dans votre audit</span>
                                        </div>
                                        <p className="text-sm text-gray-700 mb-4 leading-relaxed">
                                            Le bloc <strong>au-dessus</strong> affiche votre score global et les 5 cartes (SEO, Performance, Technique, Marché, IA). Cliquez sur une carte pour le détail.
                                        </p>
                                        <button
                                            type="button"
                                            onClick={() => setOnboardingStep(2)}
                                            className="onboarding-btn cta-audit-gratuit w-full py-2.5 px-4 rounded-xl font-semibold text-white transition-all duration-200"
                                        >
                                            Suivant →
                                        </button>
                                    </>
                                )}
                                {onboardingStep === 2 && (
                                    <>
                                        <div className="flex items-center gap-2 mb-3">
                                            <div className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center onboarding-sparkle border border-neutral-200/60">
                                                <MousePointer size={16} className="text-neutral-600" />
                                            </div>
                                            <span className="font-semibold text-gray-800">Explorez le détail</span>
                                        </div>
                                        <p className="text-sm text-gray-700 mb-4 leading-relaxed">
                                            Naviguez dans les <strong>onglets</strong> en haut (Synthèse, Technique, SEO, Performance, IA, Marché…) afin de découvrir les détails de chaque point.
                                        </p>
                                        <button
                                            type="button"
                                            onClick={finishOnboarding}
                                            className="onboarding-btn cta-audit-gratuit w-full py-2.5 px-4 rounded-xl font-semibold text-white transition-all duration-200"
                                        >
                                            Compris, c'est parti
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Modal conversion — après avoir vu tous les onglets ou clic CTA "Prendre rendez-vous" */}
            {showConversionModal && createPortal(
                <div className="fixed inset-0 z-[10000] conversion-modal-backdrop-in" style={{ isolation: 'isolate' }}>
                    <div
                        className="absolute inset-0 bg-black/50 backdrop-blur-md"
                        aria-hidden
                        onClick={() => closeConversionModal(false)}
                    />
                    <div
                        className="absolute bottom-0 left-0 right-0 sm:bottom-auto sm:top-1/2 sm:left-1/2 sm:w-full sm:max-w-md sm:-translate-x-1/2 sm:-translate-y-1/2 sm:px-4"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="conversion-modal-title"
                    >
                        <div
                            className="rounded-t-2xl sm:rounded-2xl shadow-2xl border border-white/80 bg-white/95 backdrop-blur-md p-6 relative conversion-modal-card-in"
                            style={{ boxShadow: '0 25px 50px -12px rgba(0,0,0,0.2), 0 0 0 1px rgba(255,255,255,0.8)' }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button
                                type="button"
                                onClick={() => closeConversionModal(false)}
                                className="absolute right-3 top-3 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                                aria-label="Fermer"
                            >
                                <X size={20} />
                            </button>

                            {conversionModalStep === 'success' ? (
                                <>
                                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-green-100 text-green-600 mb-4">
                                        <CheckCircle size={28} />
                                    </div>
                                    <h2 id="conversion-modal-title" className="text-xl font-bold text-gray-900 pr-8 mb-2">
                                        Demande envoyée
                                    </h2>
                                    <p className="text-gray-600 text-sm leading-relaxed mb-5">
                                        Merci ! Un expert vous recontactera sous 24h pour échanger sur vos résultats et les actions prioritaires.
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => closeConversionModal(false)}
                                        className="w-full py-3 px-4 rounded-full font-semibold text-sm border-2 border-gray-200 text-gray-700 hover:bg-gray-50 transition"
                                    >
                                        Fermer
                                    </button>
                                </>
                            ) : conversionModalStep === 'form' ? (
                                <>
                                    <h2 id="conversion-modal-title" className="text-xl font-bold text-gray-900 pr-8 mb-2">
                                        Être rappelé par un expert
                                    </h2>
                                    <p className="text-gray-600 text-sm leading-relaxed mb-4">
                                        Indiquez vos préférences. Nous vous recontactons sous 24h.
                                    </p>
                                    <div className="space-y-4 mb-5">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Créneau préféré</label>
                                            <select
                                                value={callbackSlot}
                                                onChange={(e) => setCallbackSlot(e.target.value)}
                                                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-900 text-sm focus:ring-2 focus:ring-accent/30 focus:border-accent outline-none"
                                            >
                                                <option value="indifferent">Indifférent</option>
                                                <option value="matin">Matin</option>
                                                <option value="apres_midi">Après-midi</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Message (optionnel)</label>
                                            <textarea
                                                value={callbackMessage}
                                                onChange={(e) => setCallbackMessage(e.target.value)}
                                                placeholder="Précisez votre besoin ou vos questions..."
                                                rows={3}
                                                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-900 text-sm placeholder:text-gray-400 focus:ring-2 focus:ring-accent/30 focus:border-accent outline-none resize-none"
                                            />
                                        </div>
                                        {callbackError && (
                                            <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{callbackError}</p>
                                        )}
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setConversionModalStep('intro')}
                                            className="flex-1 py-3 px-4 rounded-full font-semibold text-sm border-2 border-gray-200 text-gray-700 hover:bg-gray-50 transition"
                                        >
                                            Retour
                                        </button>
                                        <button
                                            type="button"
                                            onClick={submitCallbackRequest}
                                            disabled={callbackSubmitLoading}
                                            className="flex-1 cta-audit-gratuit inline-flex items-center justify-center gap-2 py-3 px-4 rounded-full font-semibold text-white text-sm transition-all duration-300 min-h-[48px] disabled:opacity-70"
                                        >
                                            {callbackSubmitLoading ? <Loader2 size={18} className="animate-spin" /> : 'Envoyer'}
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <h2 id="conversion-modal-title" className="text-xl font-bold text-gray-900 pr-8 mb-2">
                                        Vous avez exploré tout votre audit
                                    </h2>
                                    <p className="text-gray-600 text-sm leading-relaxed mb-5">
                                        Passez à l'action pour améliorer votre visibilité et votre référencement. Nos experts peuvent vous accompagner pour prioriser les corrections et faire monter votre site dans les résultats Google.
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => { trackEvent({ token, eventType: 'cta_click', eventLabel: 'conversion_modal_etre_rappele' }); setConversionModalStep('form'); }}
                                        className="cta-audit-gratuit inline-flex items-center justify-center gap-2 w-full py-4 px-5 rounded-full font-semibold text-white text-sm transition-all duration-300 min-h-[52px] mb-3"
                                    >
                                        Parler avec un expert
                                        <ArrowRight size={18} />
                                    </button>
                                    <a
                                        href={ctaLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={() => closeConversionModal(true)}
                                        className="inline-flex items-center justify-center gap-2 w-full py-3 text-sm font-medium text-gray-600 hover:text-gray-800 transition"
                                    >
                                        <CalendarCheck size={16} />
                                        Ou réserver un créneau en ligne
                                    </a>
                                </>
                            )}
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Top bar — au-dessus de l'overlay en étape 2 onboarding pour rester visible */}
            <div className={`fixed top-0 left-0 right-0 transition-all duration-300 ${onboardingStep === 2 ? 'z-[70]' : 'z-50'}`}>
                <div
                    className={isScrolled ? 'border-b border-white/80 shadow-sm' : ''}
                    style={isScrolled ? {
                        background: 'rgba(255, 255, 255, 0.65)',
                        backdropFilter: 'blur(12px)',
                        WebkitBackdropFilter: 'blur(12px)',
                        boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.08), 0 2px 8px 0 rgba(0, 0, 0, 0.04)',
                    } : {}}
                >
                    {/* Une seule ligne : logo | (mobile: burger+onglet | desktop: onglets) | CTA (desktop only) */}
                    <div className="px-4 md:px-6 py-3 flex items-center gap-3 md:gap-4">
                        <button
                            type="button"
                            onClick={() => handleTabChange('synthese')}
                            className={`flex items-center gap-2 flex-shrink-0 min-w-0 transition-opacity duration-300 rounded-lg -ml-1 pl-1 pr-1 py-1 hover:bg-black/5 active:bg-black/10 touch-manipulation text-left ${onboardingStep === 2 ? 'opacity-40' : ''}`}
                            aria-label="Retour à la synthèse"
                        >
                            {faviconUrl && (
                                <div className="w-8 h-8 rounded-lg flex items-center justify-center overflow-hidden backdrop-blur-sm bg-white/70 border border-white/80 flex-shrink-0">
                                    <img src={faviconUrl} alt="" className="w-full h-full object-contain" />
                                </div>
                            )}
                            <div className="min-w-0">
                                <div className="font-bold text-gray-900 truncate text-sm max-w-[140px] sm:max-w-[220px] md:max-w-none">{domain || 'Audit SEO'}</div>
                                <div className="text-[10px] text-neutral-400 hidden sm:block">by Ascanio</div>
                            </div>
                        </button>

                        {/* Mobile : slider type [ ← | Titre onglet | → ] */}
                        {(() => {
                            const currentIndex = visibleTabs.findIndex((t) => t.key === activeTab);
                            const currentTab = visibleTabs[currentIndex] || visibleTabs[0];
                            const prevKey = currentIndex > 0 ? visibleTabs[currentIndex - 1].key : null;
                            const nextKey = currentIndex < visibleTabs.length - 1 && currentIndex >= 0 ? visibleTabs[currentIndex + 1].key : null;
                            return (
                                <div
                                    className={`flex md:hidden flex-1 min-w-0 items-center justify-between gap-2 rounded-full py-1.5 px-1 ${onboardingStep === 2 ? 'onboarding-nav-highlight border-2 border-neutral-300 ring-2 ring-neutral-400/40' : 'bg-white/50 border border-white/80 backdrop-blur-md'}`}
                                    role="tablist"
                                    aria-label="Onglets de l'audit"
                                >
                                    <button
                                        type="button"
                                        onClick={() => prevKey && handleTabChange(prevKey)}
                                        disabled={!prevKey}
                                        aria-label="Onglet précédent"
                                        className="flex items-center justify-center w-10 h-10 flex-shrink-0 rounded-full text-gray-600 disabled:opacity-30 disabled:pointer-events-none active:bg-white/70 touch-manipulation"
                                    >
                                        <ChevronLeft size={22} strokeWidth={2.5} />
                                    </button>
                                    <span className="flex-1 min-w-0 text-center font-heading text-sm font-semibold text-gray-900 truncate px-1 flex items-center justify-center gap-1.5">
                                        {currentTab.locked && <Lock size={14} className="flex-shrink-0 opacity-70" aria-hidden />}
                                        {currentTab.mobileLabel || currentTab.label}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => nextKey && handleTabChange(nextKey)}
                                        disabled={!nextKey}
                                        aria-label="Onglet suivant"
                                        className="flex items-center justify-center w-10 h-10 flex-shrink-0 rounded-full text-gray-600 disabled:opacity-30 disabled:pointer-events-none active:bg-white/70 touch-manipulation"
                                    >
                                        <ChevronRight size={22} strokeWidth={2.5} />
                                    </button>
                                </div>
                            );
                        })()}

                        {/* Desktop : onglets à gauche du bouton CTA */}
                        <div ref={onboardingNavRef} className="hidden md:flex flex-1 min-w-0 items-center justify-end">
                            <nav className="no-scrollbar flex gap-0.5 overflow-x-auto p-1 rounded-full bg-[#ffffff]/50 border border-white backdrop-blur-md" style={{ WebkitOverflowScrolling: 'touch' }}>
                                {visibleTabs.map((tab) => {
                                    const isActive = activeTab === tab.key;
                                    const isLocked = tab.locked === true;
                                    return (
                                        <button
                                            key={tab.key}
                                            type="button"
                                            onClick={() => handleTabChange(tab.key)}
                                            className={`flex items-center justify-center gap-1.5 px-3 py-2 font-heading text-sm font-semibold rounded-full whitespace-nowrap flex-shrink-0 outline-none ${isActive
                                                ? 'bg-white text-black shadow-sm'
                                                : 'bg-transparent text-[#707070] hover:text-gray-800'
                                            }`}
                                        >
                                            {isLocked && <Lock size={12} className="flex-shrink-0 opacity-70" aria-hidden />}
                                            {tab.label}
                                        </button>
                                    );
                                })}
                            </nav>
                        </div>

                        {/* CTA desktop navbar — même action que les autres CTA (lien externe). */}
                        <a
                            href={ctaLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => trackEvent({ token, eventType: 'cta_click', eventLabel: 'cta_sticky_bar' })}
                            className={`hidden md:inline-flex flex-shrink-0 items-center gap-1.5 px-5 py-2.5 text-white font-semibold rounded-xl text-sm transition-all duration-300 cta-audit-gratuit ${onboardingStep === 2 ? 'opacity-40' : ''}`}
                        >
                            Améliorer mon site
                            <ArrowRight size={13} />
                        </a>
                    </div>
                </div>
            </div>

            {/* Barre sticky CTA en bas — mobile uniquement, visible après scroll */}
            <div
                className={`fixed bottom-0 left-0 right-0 z-40 md:hidden transition-transform duration-300 ease-out ${
                    isScrolled && onboardingStep !== 2 ? 'translate-y-0' : 'translate-y-full'
                }`}
                style={{
                    paddingBottom: 'env(safe-area-inset-bottom, 0)',
                }}
            >
                <div className="bg-white/95 backdrop-blur-md border-t border-neutral-200/80 px-4 py-3 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
                    <a
                        href={ctaLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => trackEvent({ token, eventType: 'cta_click', eventLabel: 'cta_sticky_bar' })}
                        className="cta-audit-gratuit w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-xl font-semibold text-white text-sm touch-manipulation"
                    >
                        Améliorer mon site
                        <ArrowRight size={16} />
                    </a>
                </div>
            </div>

            <div className="max-w-6xl mx-auto flex flex-col items-center justify-center relative z-10 pt-6 sm:pt-8 px-4 sm:px-6 w-full">
                <div className="flex flex-col gap-4 sm:gap-6 w-full">

                    {/* ── SYNTHÈSE ───────────────────────────────────────── */}
                    {activeTab === 'synthese' && (() => {
                        const nbIssues = (audit.score_title < 90 ? 1 : 0) + (audit.score_meta < 90 ? 1 : 0) + (audit.score_h1 < 90 ? 1 : 0)
                            + (audit.score_images < 90 ? 1 : 0) + (audit.score_canonical < 90 ? 1 : 0);
                        const syntheseItems = [
                            {
                                key: 'seo', icon: Search, label: 'Référencement SEO', score: Math.round(scoreSeo),
                                summary: nbIssues > 0 ? `${nbIssues} balise${nbIssues > 1 ? 's' : ''} à corriger` : 'Balises bien configurées',
                                desc: 'Titre, meta description, H1, images — ce que Google lit en premier.',
                            },
                            {
                                key: 'performance', icon: Gauge, label: 'Vitesse & Performance', score: Math.round(scorePerformance),
                                summary: `Desktop ${Math.round(Number(perfDesktop))}/100 · Mobile ${Math.round(Number(perfMobile))}/100`,
                                desc: 'Temps de chargement, accessibilité et bonnes pratiques.',
                            },
                            {
                                key: 'technique', icon: Wrench, label: 'Fondations techniques', score: scoreTechnique,
                                summary: `${techChecks.filter(Boolean).length}/7 vérifications conformes`,
                                desc: 'HTTPS, mobile, indexation, données structurées, canonical, llm.txt.',
                            },
                            {
                                key: 'marche', icon: TrendingUp, label: 'Visibilité sur Google',
                                score: audit.semrush_data?.organic_traffic != null ? scoreMarche : null,
                                summary: audit.semrush_data?.organic_traffic != null
                                    ? `${(audit.semrush_data.organic_traffic || 0).toLocaleString('fr-FR')} visites/mois · ${(audit.semrush_data.organic_keywords || 0).toLocaleString('fr-FR')} mots-clés`
                                    : 'Non disponible — non inclus dans cet audit gratuit.',
                                desc: 'Trafic organique et mots-clés positionnés sur Google.',
                            },
                            {
                                key: 'visuel', icon: Eye, label: 'Design & UX visuel',
                                score: hasVisuelData ? scoreVisuel : null,
                                summary: hasVisuelData ? 'Typographie, couleurs, boutons, modernité' : 'Non disponible sur les anciens audits',
                                desc: 'Critères UI/UX : typographie, contrastes, boutons, hiérarchie, modernité.',
                            },
                            {
                                key: 'ia', icon: Sparkles, label: 'Analyse IA du contenu',
                                score: null,
                                summary: 'Analyse du message et du positionnement',
                                desc: 'Secteur, cible, proposition de valeur et recommandations.',
                            },
                        ].filter((item) => item.key !== 'visuel' || hasVisuelData);

                        return (
                        <div className={isFirstVisit ? 'flex flex-col gap-6' : 'tab-panel-enter flex flex-col gap-6'}>
                            {/* ── Bandeau identité ─────────────────────────── */}
                            <div className={isFirstVisit ? 'block-enter' : ''} style={isFirstVisit ? { animationDelay: '0ms' } : undefined}>
                                <div
                                    className="rounded-2xl px-4 py-3.5 sm:px-6 sm:py-4.5 flex items-center justify-between gap-3.5 flex-wrap"
                                    style={card}
                                >
                                    {/* Pill site audité */}
                                    <div
                                        className="group inline-flex items-center gap-3 transition-all duration-200"
                                    >
                                        {faviconUrl ? (
                                            <img src={faviconUrl} alt="" className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl flex-shrink-0 shadow-sm" />
                                        ) : (
                                            <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-accent/10 flex items-center justify-center flex-shrink-0">
                                                <Globe size={18} className="text-accent" />
                                            </div>
                                        )}
                                        <div className="min-w-0">
                                            <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-400 leading-none mb-1">Votre site</div>
                                            <div className="font-bold text-lg sm:text-xl text-gray-900 truncate max-w-[52vw] sm:max-w-sm group-hover:text-accent transition-colors leading-tight">
                                                {domain || audit.url}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Salutation + date */}
                                    <div className="text-right">
                                        {audit.name && (
                                            <p className="text-sm sm:text-base font-semibold text-gray-800 leading-tight">Bonjour {audit.name}</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* ── BLOC 1+2 : Hero Verdict + 5 accès rapides ── */}
                            <div ref={syntheseHeroRef} className={isFirstVisit ? 'block-enter rounded-2xl shadow-xl border overflow-hidden backdrop-blur-sm' : 'rounded-2xl shadow-xl border overflow-hidden backdrop-blur-sm'} style={isFirstVisit ? { ...card, animationDelay: `${BLOCK_STAGGER_MS}ms` } : card}>
                                <div className="flex flex-col md:flex-row">
                                    {/* Colonne gauche : jauge + verdict */}
                                    <div className="flex flex-col items-center justify-center text-center p-4 sm:p-5 md:p-6 md:py-10 md:px-8 md:w-[46%] md:border-r border-white/60">
                                        <GamifiedScoreGauge
                                            mainScore={scoreGlobal}
                                            subScores={[scoreSeo, scorePerformance, scoreTechnique, ...(hasMarcheData ? [scoreMarche] : []), ...(hasVisuelData ? [scoreVisuel] : [])]}
                                            subColors={SYNTHESE_TAB_COLORS}
                                            subLabels={syntheseItems.filter((s) => s.score != null).map((s) => s.label)}
                                            hoveredIndex={syntheseHoveredIndex}
                                            onArcHover={setSyntheseHoveredIndex}
                                            size={180}
                                        />
                                        <div className="mt-3 mb-1.5">
                                            <span className="px-3 py-1 rounded-full text-sm font-bold" style={scoreLabelStyle}>{scoreLabel}</span>
                                        </div>
                                        <h2 className="text-base md:text-xl font-bold text-gray-900 mt-2 mb-1.5 leading-tight">
                                            {scoreGlobal >= 90
                                                ? 'Votre site est en bonne santé'
                                                : scoreGlobal >= 60
                                                    ? 'Votre site a du potentiel, mais perd des visiteurs'
                                                    : 'Votre site a besoin d\'attention'}
                                        </h2>
                                        <p className="text-xs md:text-sm text-neutral-500 leading-relaxed">
                                            {scoreGlobal >= 90
                                                ? 'Les fondations sont solides. Quelques optimisations ciblées peuvent vous faire devancer vos concurrents.'
                                                : scoreGlobal >= 60
                                                    ? 'Des erreurs freinent votre visibilité. Chaque jour sans correction, ce sont des visiteurs qui vont chez vos concurrents.'
                                                    : 'Des lacunes critiques empêchent Google de référencer correctement votre site.'}
                                        </p>
                                        {synthesePreviewSrc && (
                                            <div className="mt-4 w-full max-w-[300px] rounded-xl border border-white/80 bg-white/70 backdrop-blur-sm p-2 shadow-sm">
                                                <div className="text-[11px] text-gray-500 font-medium mb-2">Aperçu de votre site</div>
                                                <div className="rounded-lg overflow-hidden border border-gray-200/70 bg-white">
                                                    <img
                                                        src={synthesePreviewSrc}
                                                        alt="Aperçu du site analysé"
                                                        className="w-full h-[120px] object-cover object-top"
                                                        loading="lazy"
                                                        referrerPolicy="no-referrer"
                                                        onError={(e) => {
                                                            e.currentTarget.style.display = 'none';
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Colonne droite : 5 accès rapides — apparition en cascade gauche → droite */}
                                    <div className="flex flex-col justify-center p-3 sm:p-4 md:p-8 md:w-[54%] divide-y divide-white/60">
                                        {syntheseItems.map(({ key, icon: Icon, label, score, summary }, idx) => {
                                            const hasScore = score !== null && score !== undefined;
                                            const tabColor = SYNTHESE_TAB_COLORS[idx] ?? '#94a3b8';
                                            const tabBg = `${tabColor}22`; // fond léger dérivé de la couleur onglet
                                            const StatusIcon = hasScore ? (score >= 90 ? CheckCircle : score >= 60 ? AlertTriangle : XCircle) : BarChart3;
                                            const statusColor = hasScore
                                                ? (score >= 90 ? 'text-green-500' : score >= 60 ? 'text-amber-500' : 'text-red-400')
                                                : 'text-neutral-400';
                                            const isHighlighted = syntheseHoveredIndex === idx;
                                            return (
                                                <div key={key} className="synthese-card-enter" style={{ animationDelay: `${idx * 100}ms` }}>
                                                <button
                                                    type="button"
                                                    onClick={() => handleTabChange(key)}
                                                    onMouseEnter={() => setSyntheseHoveredIndex(idx)}
                                                    onMouseLeave={() => setSyntheseHoveredIndex(null)}
                                                    className="flex items-center gap-3 px-3 md:px-5 py-3 md:py-3.5 text-left transition-all duration-150 group rounded-xl w-full"
                                                    style={{
                                                        background: isHighlighted ? 'rgba(255,255,255,0.8)' : undefined,
                                                        boxShadow: isHighlighted ? `inset 0 0 0 2px ${tabColor}50` : undefined,
                                                    }}
                                                >
                                                    <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: tabBg }}>
                                                        <Icon size={17} style={{ color: tabColor }} />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center justify-between gap-2 mb-1">
                                                            <span className="text-sm font-bold text-gray-900 truncate flex items-center gap-1.5">
                                                                {key === 'marche' && <Lock size={12} className="flex-shrink-0 opacity-70" aria-hidden />}
                                                                {label}
                                                            </span>
                                                            {hasScore && (
                                                                <span className="text-sm font-extrabold tabular-nums flex-shrink-0" style={{ color: tabColor }}>
                                                                    {score}<span className="text-xs font-normal text-neutral-400">/100</span>
                                                                </span>
                                                            )}
                                                        </div>
                                                        {hasScore && (
                                                            <div className="w-full h-1.5 rounded-full bg-neutral-100 overflow-hidden">
                                                                <AnimatedProgressBar percent={score} duration={900} delay={idx * 100} color={tabColor} />
                                                            </div>
                                                        )}
                                                        <div className="flex items-center gap-1.5 mt-1">
                                                            <span className="text-xs text-neutral-500 truncate">{summary}</span>
                                                        </div>
                                                    </div>
                                                    <ArrowRight size={15} className="text-neutral-300 group-hover:text-neutral-500 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                                                </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>

                            {/* ── BLOC 4 : Recommandations personnalisées (IA) — 3 visibles + blur progressif ── */}
                            {iaData?.improvements?.length > 0 && (() => {
                                const allItems = [...(iaData.improvements || []), ...FAKE_IMPROVEMENTS];
                                const visibleItems = allItems.slice(0, 2);
                                const blurredItems = allItems.slice(2);

                                const ImprovementRow = ({ imp, index, blurPx = 0 }) => {
                                    const pr = imp.priority || 'moyenne';
                                    const pColor = pr === 'haute' ? '#ef4444' : pr === 'faible' ? '#16a34a' : '#d97706';
                                    const pBg = pr === 'haute' ? '#fef2f2' : pr === 'faible' ? '#f0fdf4' : '#fffbeb';
                                    const pBorder = pr === 'haute' ? '#fecaca' : pr === 'faible' ? '#bbf7d0' : '#fde68a';
                                    return (
                                        <div
                                            key={index}
                                            className="flex items-start gap-3 p-3 rounded-xl border"
                                            style={{ background: pBg, borderColor: pBorder, filter: blurPx ? `blur(${blurPx}px)` : undefined }}
                                        >
                                            <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: `${pColor}20` }}>
                                                <span className="text-xs font-bold" style={{ color: pColor }}>{index + 1}</span>
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-semibold text-sm text-gray-900">{imp.title}</span>
                                                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase" style={{ color: pColor, background: `${pColor}18` }}>{pr}</span>
                                                </div>
                                                <div className="text-xs text-gray-500 mt-0.5">{imp.explanation}</div>
                                            </div>
                                        </div>
                                    );
                                };

                                return (
                                    <div className={isFirstVisit ? 'block-enter' : ''} style={isFirstVisit ? { animationDelay: `${2 * BLOCK_STAGGER_MS}ms` } : undefined}>
                                        <div className="rounded-2xl p-6 shadow-xl border" style={card}>
                                            <h3 className="text-base font-bold text-gray-900 mb-4">Recommandations personnalisées</h3>
                                            <div className="space-y-3">
                                                {visibleItems.map((imp, i) => (
                                                    <ImprovementRow key={i} imp={imp} index={i} />
                                                ))}
                                            </div>
                                            {blurredItems.length > 0 && (
                                                <BlurredSection
                                                    title="En parler avec un expert"
                                                    description="Prenez rendez-vous pour échanger sur les points à corriger en priorité et sur la stratégie à mettre en place."
                                                    ctaLabel="Prendre rendez-vous"
                                                    ctaLink={ctaLink}
                                                    onCtaClick={() => trackEvent({ token, eventType: 'cta_click', eventLabel: 'cta_blurred_synthese_rdv' })}
                                                    blurAmount={0}
                                                    compact
                                                    className="mt-2"
                                                >
                                                    <div className="space-y-3 pt-2 -mt-2">
                                                        {blurredItems.map((imp, i) => (
                                                            <ImprovementRow key={i} imp={imp} index={2 + i} blurPx={2 + i * 2} />
                                                        ))}
                                                    </div>
                                                </BlurredSection>
                                            )}
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                        );
                    })()}

                    {/* ── DONNÉES DE MARCHÉ (Semrush) ───────────────────── */}
                    {activeTab === 'marche' && (
                        <div className="tab-panel-enter flex flex-col gap-6">
                            {audit.semrush_data ? (
                                <>
                                    <TabScoreHeader
                                        score={scoreMarche}
                                        title="Visibilité sur Google"
                                        badge={scoreMarche >= 90 ? 'Bonne visibilité' : scoreMarche >= 60 ? 'En croissance' : scoreMarche >= 40 ? 'Débutant' : scoreMarche > 0 ? 'Faible' : 'Non mesuré'}
                                        explanation={
                                            <div className="space-y-5">
                                                <p className="text-neutral-600 leading-relaxed">{TAB_EXPLANATIONS.marche}</p>
                                                <div>
                                                    <h4 className="text-sm font-semibold text-neutral-800 mb-2">Comment lire ces chiffres</h4>
                                                    <p className="text-sm text-neutral-600 mb-4">Les données sont des estimations Semrush. Voici ce que signifient les indicateurs clés :</p>
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                        {MARCHE_GUIDES.map(({ label, short, icon: Icon }) => (
                                                            <div key={label} className="flex items-center gap-3 p-3 rounded-xl backdrop-blur-sm bg-white/50 border border-white/60">
                                                                <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(14,165,233,0.1)' }}>
                                                                    <Icon size={18} className="text-sky-600" />
                                                                </div>
                                                                <div className="min-w-0 flex-1">
                                                                    <div className="font-semibold text-gray-900 text-sm">{label}</div>
                                                                    <div className="text-xs text-neutral-500 mt-0.5 leading-snug">{short}</div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                                <div className="p-3 rounded-xl bg-amber-50/80 border border-amber-200/60">
                                                    <p className="text-xs text-amber-900 leading-relaxed">
                                                        <strong className="font-semibold">Repères :</strong> Un site e-commerce attire souvent 5 000 à 50 000 visites organiques/mois. Un site vitrine : 100 à 2 000. Ces ordres de grandeur varient selon le secteur.
                                                    </p>
                                                </div>
                                            </div>
                                        }
                                        subtitle={audit.semrush_data ? "Données issues des estimations Semrush" : null}
                                        variant={scoreMarche >= 90 ? 'good' : scoreMarche >= 60 ? 'warning' : 'bad'}
                                        cardStyle={card}
                                        interpretation={audit.tab_interpretations?.marche || (
                                            (audit.semrush_data.organic_traffic || 0) > 1000 ? (
                                                <><strong>Bonne visibilité organique :</strong> Votre site génère un trafic significatif depuis Google. Vous êtes positionné sur {(audit.semrush_data.organic_keywords || 0).toLocaleString('fr-FR')} mots-clés.</>
                                            ) : (audit.semrush_data.organic_traffic || 0) > 100 ? (
                                                <><strong>Visibilité en croissance :</strong> Votre site commence à apparaître dans les résultats Google. Un travail sur le contenu et les mots-clés peut accélérer la croissance.</>
                                            ) : (
                                                <><strong>Potentiel à développer :</strong> Votre site est encore peu visible. Une stratégie SEO structurée permettra de faire décoller le trafic.</>
                                            )
                                        )}
                                        belowInterpretation={<MarcheMetricCards semrushData={audit.semrush_data} />}
                                    />
                                    <div className="rounded-2xl overflow-hidden shadow-xl border p-6 mb-6" style={card}>
                                        <BlurredSection
                                                compact
                                                title="Recommandation détaillées"
                                                description="Liste complète de vos mots-clés, analyse de la concurrence et opportunités de croissance identifiées."
                                                ctaLabel="Améliorer ma visibilité"
                                                ctaLink={ctaLink}
                                                onCtaClick={() => trackEvent({ token, eventType: 'cta_click', eventLabel: 'cta_blurred_marche' })}
                                            >
                                                <div className="p-4 space-y-3">
                                                    <div className="h-4 bg-gray-200/50 rounded w-3/4" />
                                                    <div className="h-4 bg-gray-200/50 rounded w-full" />
                                                    <div className="h-4 bg-gray-200/50 rounded w-5/6" />
                                                    <p className="text-sm text-gray-500 mt-2">Mots-clés · Concurrence · Opportunités</p>
                                                </div>
                                            </BlurredSection>
                                    </div>
                                </>
                            ) : (
                                /* Onglet Visibilité Google — teaser pour audit gratuit */
                                <div className="tab-panel-enter flex flex-col gap-6">
                                    <div className="rounded-2xl shadow-xl border overflow-hidden" style={card}>
                                        <div className="p-6 md:p-8 lg:p-10">
                                            <div className="flex flex-col md:flex-row md:items-start gap-6 md:gap-8">
                                                <div className="min-w-0 flex-1">
                                                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold mb-4 bg-amber-50 text-amber-800 border border-amber-200/70">
                                                        <Lock size={12} /> Contenu premium
                                                    </div>
                                                    <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">
                                                        Visibilité sur Google : où en est vraiment votre site ?
                                                    </h2>
                                                    <p className="text-neutral-600 leading-relaxed mb-5">
                                                        La plupart des visiteurs vous trouvent via Google — mais combien ? Sur quels mots-clés êtes-vous visible ? Comment vous situez-vous face à vos concurrents ? Ces réponses sont essentielles pour prioriser vos efforts et faire grandir votre trafic.
                                                    </p>
                                                    <div className="mb-6">
                                                        <h3 className="text-sm font-semibold text-gray-900 mb-3">Avec l'audit complet, vous découvrirez :</h3>
                                                        <ul className="space-y-2.5">
                                                            {[
                                                                { icon: BarChart3, text: 'Votre trafic organique estimé (visiteurs/mois venant de Google)', color: '#0ea5e9' },
                                                                { icon: Hash, text: 'La liste de vos mots-clés positionnés et leur évolution', color: '#6366f1' },
                                                                { icon: Globe, text: 'Votre position par rapport à la concurrence sur votre secteur', color: '#059669' },
                                                                { icon: ArrowUp, text: 'Les opportunités concrètes pour monter dans les résultats', color: '#d97706' },
                                                            ].map(({ icon: Icon, text, color }) => (
                                                                <li key={text} className="flex items-start gap-3">
                                                                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: `${color}15` }}>
                                                                        <Icon size={16} style={{ color }} />
                                                                    </div>
                                                                    <span className="text-sm text-neutral-700 leading-snug">{text}</span>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                    <p className="text-sm text-neutral-500 mb-6">
                                                        Données issues d'outils professionnels (Semrush). Idéal pour fixer des objectifs réalistes et mesurer vos progrès.
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="rounded-xl p-4 bg-neutral-50/80 border border-neutral-200/60">
                                                <p className="text-xs text-neutral-600 leading-relaxed">
                                                    <strong className="text-neutral-800">Pourquoi c'est important :</strong> Sans mesurer votre visibilité, vous avancez à l'aveugle. Connaître vos mots-clés et votre trafic permet d'ajuster votre contenu, votre SEO et vos priorités pour attirer plus de clients qualifiés.
                                                </p>
                                            </div>
                                            <div className="flex justify-end pt-4 mt-4 border-t border-neutral-200/60">
                                                <a
                                                    href={ctaLink}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={() => trackEvent({ token, eventType: 'cta_click', eventLabel: 'cta_marche_teaser' })}
                                                    className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full font-semibold text-white text-sm cta-audit-gratuit transition-all duration-300 hover:opacity-95"
                                                >
                                                    Découvrir ma visibilité Google
                                                    <ArrowRight size={18} />
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── SEO ────────────────────────────────────────── */}
                    {activeTab === 'seo' && (
                        <div className="tab-panel-enter flex flex-col gap-6">
                            {/* Score à gauche, explication à droite */}
                            <TabScoreHeader
                                score={scoreSeo}
                                title="Score SEO"
                                badge={scoreSeo >= 90 ? 'Bien optimisé' : scoreSeo >= 60 ? 'À améliorer' : 'Insuffisant'}
                                explanation={TAB_EXPLANATIONS.seo}
                                variant={scoreSeo >= 90 ? 'good' : scoreSeo >= 60 ? 'warning' : 'bad'}
                                cardStyle={card}
                                interpretation={audit.tab_interpretations?.seo || (
                                    scoreSeo >= 90 ? (
                                        <><strong>SEO bien optimisé :</strong> Les balises essentielles (title, meta, H1) sont correctement configurées. Votre page envoie les bons signaux à Google.</>
                                    ) : scoreSeo >= 60 ? (
                                        <><strong>SEO à améliorer :</strong> Certaines balises clés sont manquantes ou mal optimisées. Corrigez les points signalés ci-dessous pour gagner en visibilité.</>
                                    ) : (
                                        <><strong>SEO insuffisant :</strong> Plusieurs éléments essentiels au référencement sont absents. Google a du mal à comprendre et classer votre page.</>
                                    )
                                )}
                                belowInterpretation={<GooglePreviewCard audit={audit} />}
                            />


                            <div className="rounded-2xl p-6 shadow-xl border" style={card}>
                                <h2 className="text-lg font-bold text-gray-900 mb-1 flex items-center gap-2">
                                    Analyse Sémantique : ce que lit Google
                                </h2>
                                <p className="text-sm text-gray-500 mb-2">Analyse de la page <strong>{audit.url}</strong></p>

                                <div className="space-y-2">
                                    {[
                                        {
                                            key: 'title',
                                            icon: Type,
                                            label: 'Balise Title',
                                            value: audit.title ? `${audit.title_length || audit.title.length} caractères` : null,
                                            ok: audit.score_title >= 90,
                                            warning: audit.score_title > 0 && audit.score_title < 90,
                                            content: audit.title || null,
                                            contentLabel: 'Ce texte est le lien cliquable dans les résultats Google.',
                                            explanation: !audit.title ? "Aucun titre trouvé. C'est pénalisant : c'est le texte bleu cliquable dans Google, indispensable pour attirer des visiteurs." : audit.score_title >= 90 ? "Parfait ! Votre titre est bien calibré pour s'afficher correctement sur Google." : (audit.title_length || audit.title.length) < 50 ? "Titre sous-optimisé. Visez 50–70 caractères pour insérer vos mots-clés importants." : "Titre trop long. Il sera coupé par « ... » dans les résultats, réduisant votre taux de clic."
                                        },
                                        {
                                            key: 'meta',
                                            icon: AlignLeft,
                                            label: 'Meta Description',
                                            value: audit.meta_description ? `${audit.meta_length || audit.meta_description.length} caractères` : null,
                                            ok: audit.score_meta >= 90,
                                            warning: audit.score_meta > 0 && audit.score_meta < 90,
                                            content: audit.meta_description || null,
                                            contentLabel: 'Argumentaire affiché sous votre titre dans Google.',
                                            explanation: !audit.meta_description ? "Aucune description. C'est votre argument de vente dans Google. Sans elle, le moteur de recherche choisit un extrait au hasard." : audit.score_meta >= 90 ? "Très bien ! Votre description incitera les internautes à cliquer." : (audit.meta_length || audit.meta_description.length) < 120 ? "Description trop courte pour être persuasive. Profitez de l'espace pour vous démarquer de vos concurrents." : "Description trop longue, votre argumentaire sera coupé."
                                        },
                                        {
                                            key: 'h1',
                                            icon: Heading1,
                                            label: 'Balise H1',
                                            value: audit.h1 || null,
                                            ok: audit.score_h1 >= 90 && audit.nb_h1 === 1,
                                            warning: audit.nb_h1 > 1,
                                            content: audit.h1 || null,
                                            contentLabel: 'Titre principal de la page, essentiel pour que Google comprenne votre thématique.',
                                            explanation: audit.nb_h1 === 0 ? "Aucun H1 ! C'est le grand titre de votre page. Sans lui, Google ne sait pas de quoi traite votre site." : audit.nb_h1 > 1 ? `${audit.nb_h1} H1 détectés. Cela dilue l'importance de vos mots-clés. Il ne doit y avoir qu'un seul H1.` : "Parfait ! La structure principale de votre page est claire pour Google."
                                        },
                                        {
                                            key: 'images',
                                            icon: ImageIcon,
                                            label: 'Images',
                                            value: audit.nb_images > 0 ? `${audit.nb_images_without_alt}/${audit.nb_images} sans description` : 'Aucune image',
                                            ok: audit.score_images >= 90,
                                            warning: audit.nb_images_without_alt > 0 && audit.nb_images_without_alt < audit.nb_images,
                                            content: null,
                                            contentLabel: null,
                                            explanation: audit.nb_images === 0 ? "Aucune image détectée sur la page." : audit.score_images >= 90 ? "Toutes vos images ont une description alternative. Google peut les comprendre." : `${audit.nb_images_without_alt} image${audit.nb_images_without_alt > 1 ? 's sont' : ' est'} sans texte alternatif. Google ne peut pas les « voir ».`
                                        },
                                        {
                                            key: 'canonical',
                                            icon: Link2,
                                            label: 'Balise Canonical',
                                            value: audit.has_canonical ? 'Présente' : 'Absente',
                                            ok: audit.score_canonical >= 90,
                                            warning: false,
                                            content: null,
                                            contentLabel: null,
                                            explanation: audit.has_canonical ? "La balise canonical est présente. Elle protège contre le contenu dupliqué." : "Pas de canonical. Sans elle, Google peut indexer plusieurs versions de votre page."
                                        }
                                    ].map(({ key, icon: Icon, label, value, ok, warning, content, contentLabel, explanation }) => {
                                        const isOpen = openCriterion === key;
                                        const bg = ok ? '#f0fdf4' : warning ? '#fffbeb' : '#fef2f2';
                                        const border = ok ? '#bbf7d0' : warning ? '#fde68a' : '#fecaca';
                                        const iconBg = ok ? '#dcfce7' : warning ? '#fef3c7' : '#fee2e2';
                                        const iconColor = ok ? '#16a34a' : warning ? '#d97706' : '#ef4444';
                                        return (
                                            <div key={key} className="rounded-xl border overflow-hidden" style={{ background: bg, borderColor: border }}>
                                                <button
                                                    type="button"
                                                    onClick={() => setOpenCriterion(isOpen ? null : key)}
                                                    className="w-full flex items-center gap-3 p-4 text-left hover:opacity-90 transition-opacity"
                                                >
                                                    <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: iconBg }}>
                                                        <Icon size={18} style={{ color: iconColor }} />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <span className="font-semibold text-gray-900 text-sm">{label}</span>
                                                        {value && <span className="text-neutral-500 text-xs font-mono ml-2 truncate block md:inline">{value}</span>}
                                                    </div>
                                                    {ok ? <CheckCircle size={18} className="text-green-500 flex-shrink-0" /> : warning ? <AlertTriangle size={18} className="text-amber-500 flex-shrink-0" /> : <XCircle size={18} className="text-red-400 flex-shrink-0" />}
                                                    {isOpen ? <ChevronUp size={18} className="text-neutral-400 flex-shrink-0" /> : <ChevronDown size={18} className="text-neutral-400 flex-shrink-0" />}
                                                </button>
                                                {isOpen && (
                                                    <div className="px-4 pb-4 border-t border-neutral-200/50 pt-3 space-y-3">
                                                        {content != null && (
                                                            <div className="rounded-lg backdrop-blur-sm bg-white/50 border border-white/60 p-3">
                                                                <p className="text-sm text-neutral-900 font-medium leading-snug">{content}</p>
                                                                {contentLabel && <p className="text-xs text-neutral-500 mt-1.5">{contentLabel}</p>}
                                                            </div>
                                                        )}
                                                        <p className="text-xs text-neutral-600 leading-relaxed">{explanation}</p>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Headings structure */}
                            {(() => {
                                const headingsList = Array.isArray(audit.headings_list) && audit.headings_list.length > 0
                                    ? audit.headings_list
                                    : (audit.h1 ? [{ tag: 'h1', text: audit.h1, level: 1 }] : []);
                                const visibleCount = 5;
                                const visibleHeadings = headingsList.slice(0, visibleCount);
                                const placeholderSuite = [
                                    { tag: 'h2', text: '…', level: 2 },
                                    { tag: 'h3', text: '…', level: 3 },
                                    { tag: 'h3', text: '…', level: 3 },
                                ];

                                const HeadingRow = ({ item, index, blurPx = 0 }) => (
                                    <div
                                        key={index}
                                        className="flex items-center gap-2 text-sm text-gray-600 p-2 bg-gray-50 rounded-lg"
                                        style={{
                                            paddingLeft: `${12 + (item.level - 1) * 16}px`,
                                            filter: blurPx ? `blur(${blurPx}px)` : undefined,
                                        }}
                                    >
                                        <span className="font-mono text-xs w-8 font-bold shrink-0" style={{ color: '#1e40af' }}>{(item.tag || 'h' + item.level).toUpperCase()}</span>
                                        <span className="min-w-0 truncate">{item.text || '—'}</span>
                                    </div>
                                );

                                return (
                                    <div className="rounded-2xl p-6 shadow-xl border" style={card}>
                                        <h3 className="text-lg font-bold text-gray-900 mb-4">Structure des titres (H1 &rarr; H6)</h3>
                                        <div className="space-y-2">
                                            {visibleHeadings.length === 0 ? (
                                                <div className="text-sm text-neutral-500 italic p-2">Aucune structure de titres détectée.</div>
                                            ) : (
                                                visibleHeadings.map((item, i) => <HeadingRow key={i} item={item} index={i} />)
                                            )}
                                        </div>
                                        <BlurredSection
                                            title="Suite de l'architecture"
                                            description="Prenez rendez-vous pour échanger sur les points à corriger en priorité et sur la stratégie à mettre en place."
                                            ctaLabel="Prendre rendez-vous"
                                            ctaLink={ctaLink}
                                            onCtaClick={() => trackEvent({ token, eventType: 'cta_click', eventLabel: 'cta_blurred_seo_headings' })}
                                            blurAmount={0}
                                            compact
                                        >
                                            <div className="space-y-2 pt-2 -mt-2">
                                                {placeholderSuite.map((item, i) => (
                                                    <HeadingRow key={i} item={item} index={i} blurPx={2 + i * 2} />
                                                ))}
                                            </div>
                                        </BlurredSection>
                                    </div>
                                );
                            })()}
                        </div>
                    )}

                    {/* ── PERFORMANCE ──────────────────────────────────── */}
                    {activeTab === 'performance' && (
                        <div className="tab-panel-enter flex flex-col gap-6">
                            {/* Score à gauche, explication à droite */}
                            <TabScoreHeader
                                score={scorePerformance}
                                title="Score Performance"
                                badge={scorePerformance >= 90 ? 'Excellente performance' : scorePerformance >= 60 ? 'Performance correcte' : 'À améliorer'}
                                explanation={TAB_EXPLANATIONS.performance}
                                subtitle="Pondération : Desktop 30% · Mobile 30% · Accessibilité 15% · Bonnes pratiques 15% · SEO 10%"
                                variant={scorePerformance >= 90 ? 'good' : scorePerformance >= 60 ? 'warning' : 'bad'}
                                cardStyle={card}
                                interpretation={audit.tab_interpretations?.performance || (() => {
                                    const dsk = audit.pagespeed_performance_desktop || 0;
                                    const mob = audit.pagespeed_performance_mobile || 0;
                                    const avg = (dsk + mob) / 2;
                                    const min = Math.min(dsk, mob);
                                    if (avg >= 90 && min >= 70) return <><strong>Excellente performance :</strong> Votre site est rapide sur desktop et mobile. C'est un point fort majeur pour le SEO et la conversion.</>;
                                    if (avg >= 50 && min >= 40) return <><strong>Performance moyenne :</strong> Les temps de chargement sont corrects sur desktop mais souvent insuffisants sur mobile. Des optimisations (images, scripts) amélioreraient l'expérience.</>;
                                    return <><strong>Performance insuffisante :</strong> Votre site est trop lent. Google pénalise les pages lentes et de nombreux visiteurs quittent avant le chargement. Optimiser la vitesse est prioritaire.</>;
                                })()}
                                belowInterpretation={<PerformanceMetricCards audit={audit} />}
                            />

                            {/* Core Web Vitals */}
                            {(() => {
                                const cwv = audit.core_web_vitals || {};
                                const visibleWebVitals = [
                                    { label: 'LCP', value: cwv.lcp?.displayValue || '—', desc: 'Largest Contentful Paint · Temps de chargement du contenu principal' },
                                    { label: 'CLS', value: cwv.cls?.displayValue != null ? String(cwv.cls.displayValue) : '—', desc: 'Cumulative Layout Shift · Stabilité visuelle de la page' },
                                ];
                                const hiddenWebVitals = [
                                    { label: 'INP', value: cwv.inp?.displayValue || 'Interaction to Next Paint', desc: 'Réactivité perçue aux interactions' },
                                    { label: 'TTFB', value: cwv.ttfb?.displayValue || 'Time to First Byte', desc: 'Temps de réponse du serveur' },
                                    { label: 'Recommandations', value: 'Actions prioritaires', desc: 'Optimisations personnalisées pour votre site' },
                                ];

                                const MetricRow = ({ label, value, desc, blurPx = 0 }) => (
                                    <div
                                        className="flex items-center gap-3 text-sm text-gray-600 p-3 bg-gray-50 rounded-lg"
                                        style={{ filter: blurPx ? `blur(${blurPx}px)` : undefined }}
                                    >
                                        <span className="font-mono text-xs w-10 font-bold shrink-0" style={{ color: '#1e40af' }}>{label}</span>
                                        <span className="font-semibold text-gray-900">{value}</span>
                                        {desc && <span className="text-xs text-gray-400 hidden sm:inline">{desc}</span>}
                                    </div>
                                );

                                return (
                                    <div className="rounded-2xl p-6 shadow-xl border" style={card}>
                                        <h3 className="text-lg font-bold text-gray-900 mb-4">Core Web Vitals & Recommandations</h3>
                                        <p className="text-sm text-neutral-500 mb-4">Métriques d'expérience utilisateur utilisées par Google pour le classement.</p>
                                        <div className="space-y-2">
                                            {visibleWebVitals.map((m, i) => (
                                                <MetricRow key={i} label={m.label} value={m.value} desc={m.desc} />
                                            ))}
                                        </div>
                                        <BlurredSection
                                            title="Core Web Vitals & Recommandations"
                                            description="Prenez rendez-vous pour échanger sur les points à corriger en priorité et sur la stratégie à mettre en place."
                                            ctaLabel="Prendre rendez-vous"
                                            ctaLink={ctaLink}
                                            onCtaClick={() => trackEvent({ token, eventType: 'cta_click', eventLabel: 'cta_blurred_performance' })}
                                            blurAmount={0}
                                        >
                                            <div className="space-y-2 pt-2 -mt-2">
                                                {hiddenWebVitals.map((m, i) => (
                                                    <MetricRow key={i} label={m.label} value={m.value} desc={m.desc} blurPx={2 + i * 2} />
                                                ))}
                                            </div>
                                        </BlurredSection>
                                    </div>
                                );
                            })()}
                        </div>
                    )}

                    {/* ── TECHNIQUE ────────────────────────────────────── */}
                    {activeTab === 'technique' && (
                        <div className="tab-panel-enter flex flex-col gap-6">
                            {/* Score à gauche, explication à droite */}
                            <TabScoreHeader
                                score={scoreTechnique}
                                title="Score technique"
                                badge={scoreTechnique >= 90 ? 'Bien configuré' : scoreTechnique >= 60 ? 'À améliorer' : 'Insuffisant'}
                                explanation={TAB_EXPLANATIONS.technique}
                                subtitle={`${techChecks.filter(Boolean).length}/7 vérifications conformes : HTTPS, mobile, indexation, données structurées, canonical, llm.txt`}
                                variant={scoreTechnique >= 90 ? 'good' : scoreTechnique >= 60 ? 'warning' : 'bad'}
                                cardStyle={card}
                                interpretation={audit.tab_interpretations?.technique || (
                                    scoreTechnique >= 90 ? (
                                        <><strong>Configuration technique solide :</strong> {techChecks.filter(Boolean).length}/7 vérifications techniques sont conformes. Votre site est bien configuré pour le référencement.</>
                                    ) : scoreTechnique >= 60 ? (
                                        <><strong>Configuration à améliorer :</strong> {techChecks.filter(Boolean).length}/7 vérifications sont conformes. Certains points techniques méritent votre attention.</>
                                    ) : (
                                        <><strong>Problèmes techniques détectés :</strong> Seulement {techChecks.filter(Boolean).length}/7 vérifications sont conformes. Des corrections techniques sont nécessaires.</>
                                    )
                                )}
                                belowInterpretation={<TechniqueInfoCards audit={audit} />}
                            />

                            {/* Informations techniques */}
                            <div className="rounded-2xl p-6 shadow-xl border" style={card}>
                                <h2 className="text-lg font-bold text-gray-900 mb-4">Informations techniques</h2>

                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                                    {audit.sitemap_pages_count != null && (
                                        <StatBadge icon={BookOpen} label="Nombre de pages" neutral value={`${audit.sitemap_pages_count.toLocaleString('fr-FR')}`} />
                                    )}
                                    <StatBadge icon={Globe} label="HTTPS" value={audit.is_https ? 'Sécurisé' : 'Non sécurisé'} ok={audit.is_https} />
                                    <StatBadge icon={Smartphone} label="Mobile-friendly" value={audit.has_viewport ? 'Configuré' : 'Non configuré'} ok={audit.has_viewport} />
                                    <StatBadge icon={Search} label="Indexation Google" value={audit.noindex ? 'Bloquée (noindex)' : 'Autorisée'} ok={!audit.noindex} />
                                </div>

                                <BlurredSection
                                    title="Détails et recommandations techniques"
                                    description="Prenez rendez-vous pour échanger sur les points à corriger en priorité et sur la stratégie à mettre en place."
                                    ctaLabel="Parler avec un expert"
                                    ctaLink={ctaLink}
                                    onCtaClick={() => trackEvent({ token, eventType: 'cta_click', eventLabel: 'cta_blurred_technique' })}
                                    blurAmount={3}
                                    compact
                                    className="mt-6"
                                >
                                <div className="space-y-3 w-full">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 mb-8">
                                        <StatBadge icon={Code} label="Données structurées" value={audit.has_structured_data ? 'Présentes' : 'Absentes'} ok={audit.has_structured_data} />
                                        <StatBadge icon={FileText} label="Fichier llm.txt" value={audit.has_llm_file ? 'Présent' : 'Absent'} ok={audit.has_llm_file} />
                                        <StatBadge icon={Globe} label="Langue déclarée" value={audit.lang ? `lang="${audit.lang}"` : 'Non définie'} ok={!!audit.lang} />
                                        <StatBadge icon={Link2} label="Balise canonical" value={audit.has_canonical ? 'Présente' : 'Absente'} ok={audit.has_canonical} />
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                                        {[
                                            { label: 'Schéma Schema.org', value: 'Types et propriétés détectées' },
                                            { label: 'Contenu llm.txt', value: 'Validation et recommandations' },
                                            { label: 'Headers sécurité', value: 'CSP, HSTS, X-Frame' },
                                            { label: 'Configuration indexation', value: 'robots.txt, sitemap' },
                                        ].map((item, i) => (
                                            <div
                                                key={i}
                                                className="flex items-center gap-3 px-4 py-3 rounded-xl border bg-gray-50/80 border-gray-200/60 min-w-0"
                                            >
                                                <Code size={16} className="text-blue-600 shrink-0" />
                                                <div className="min-w-0 flex-1">
                                                    <div className="text-xs text-gray-400 font-medium">{item.label}</div>
                                                    <div className="text-sm font-bold text-gray-700 leading-snug">{item.value}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                </BlurredSection>
                            </div>
                        </div>
                    )}

                    {/* ── AUDIT VISUEL ──────────────────────────────── */}
                    {activeTab === 'visuel' && (() => {
                        const va = visualData;
                        const analysis = va?.analysis || null;
                        const VISUEL_COLOR = '#e8609a';
                        const noteColor = (note) => note >= 4 ? '#16a34a' : note >= 3 ? '#d97706' : '#ef4444';
                        const noteBg = (note) => note >= 4 ? '#dcfce7' : note >= 3 ? '#fef3c7' : '#fee2e2';
                        const sortedCriteres = analysis?.criteres
                            ? [...analysis.criteres].sort((a, b) => (a.note || 0) - (b.note || 0))
                            : [];
                        const visibleCriteres = sortedCriteres.slice(0, 4);
                        const allPointsForts = Array.isArray(analysis?.points_forts) ? analysis.points_forts : [];
                        const allAxesAmelioration = Array.isArray(analysis?.axes_amelioration) ? analysis.axes_amelioration : [];
                        const visiblePointsForts = allPointsForts.slice(0, 2);
                        const hiddenPointsForts = allPointsForts.slice(2);
                        const visibleAxesAmelioration = allAxesAmelioration.slice(0, 2);
                        const hiddenAxesAmelioration = allAxesAmelioration.slice(2);
                        const visuelBadge = scoreVisuel >= 90 ? 'Design excellent' : scoreVisuel >= 60 ? 'Design correct' : 'À améliorer';
                        const previewTargetUrl = va?.url || audit?.url || '';
                        const previewSrc = storedPreviewSrc || (previewTargetUrl
                            ? `https://image.thum.io/get/width/900/noanimate/${encodeURIComponent(previewTargetUrl)}`
                            : null);
                        return (
                        <div className="tab-panel-enter flex flex-col gap-6">
                            {!hasVisuelData && (
                                <div className="rounded-2xl p-8 shadow-xl border text-center" style={card}>
                                    <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: 'rgba(232,96,154,0.12)' }}>
                                        <Eye size={32} style={{ color: VISUEL_COLOR }} strokeWidth={2} />
                                    </div>
                                    <h3 className="text-lg font-bold text-gray-900 mb-2">Audit visuel non disponible</h3>
                                    <p className="text-sm text-gray-500 max-w-md mx-auto">
                                        L'audit visuel est généré automatiquement pour les nouveaux audits. Lancez un nouvel audit pour obtenir cette analyse.
                                    </p>
                                </div>
                            )}

                            {hasVisuelData && analysis && (
                                <>
                                    {/* Hero score */}
                                    <div className="rounded-2xl overflow-hidden shadow-xl border" style={card}>
                                        <div className="p-6 md:p-8 flex flex-col md:flex-row items-center gap-6">
                                            <div className="flex flex-col items-center justify-center flex-shrink-0">
                                                <ScoreGauge value={scoreVisuel} size={126} />
                                                <span
                                                    className="mt-3 inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold"
                                                    style={{ background: 'rgba(0,0,0,0.06)', color: '#374151' }}
                                                >
                                                    {visuelBadge}
                                                </span>
                                            </div>
                                            <div className="flex-1 text-center md:text-left min-w-0">
                                                <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: VISUEL_COLOR }}>Score UI/UX visuel</p>
                                                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">Design &amp; Expérience visuelle</h2>
                                                {analysis.resume && (
                                                    <p className="text-sm text-gray-600 leading-relaxed">{analysis.resume}</p>
                                                )}
                                            </div>
                                            {previewSrc && (
                                                <div className="w-full md:w-[240px] lg:w-[280px] rounded-xl border border-white/80 bg-white/70 backdrop-blur-sm p-2 flex-shrink-0">
                                                    <div className="text-[11px] text-gray-500 font-medium mb-2 px-1">Aperçu du site analysé</div>
                                                    <div className="rounded-lg overflow-hidden border border-gray-200/70 bg-white">
                                                        <img
                                                            src={previewSrc}
                                                            alt="Aperçu du site audité"
                                                            className="w-full h-[130px] object-cover object-top"
                                                            loading="lazy"
                                                            referrerPolicy="no-referrer"
                                                            onError={(e) => {
                                                                e.currentTarget.style.display = 'none';
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Critères visibles — extrait */}
                                    {sortedCriteres.length > 0 && (
                                        <div className="rounded-2xl p-6 shadow-xl border" style={card}>
                                            <div className="flex items-center justify-between mb-4">
                                                <h3 className="text-base font-bold text-gray-900">Analyse par critère</h3>
                                                <span className="text-xs text-gray-400">Extrait gratuit</span>
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                {visibleCriteres.map((c) => (
                                                    <div key={c.cle} className="rounded-xl border p-4" style={{ background: `${noteBg(c.note)}60`, borderColor: `${noteColor(c.note)}30` }}>
                                                        <div className="flex items-start justify-between gap-2 mb-2">
                                                            <span className="text-sm font-semibold text-gray-800 leading-tight">{c.nom}</span>
                                                            <div className="flex items-center gap-1.5 flex-shrink-0">
                                                                <div className="flex gap-0.5">
                                                                    {[1,2,3,4,5].map((n) => (
                                                                        <div key={n} className="w-2.5 h-2.5 rounded-full" style={{ background: n <= c.note ? noteColor(c.note) : '#e5e7eb' }} />
                                                                    ))}
                                                                </div>
                                                                <span className="text-xs font-bold" style={{ color: noteColor(c.note) }}>{c.note}/5</span>
                                                            </div>
                                                        </div>
                                                        {c.observation && (
                                                            <p className="text-xs text-gray-600 leading-relaxed bg-white/70 rounded-lg px-2.5 py-2">{c.observation}</p>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Points forts + axes d'amélioration */}
                                    {(allPointsForts.length > 0 || allAxesAmelioration.length > 0) && (
                                        <div className="rounded-2xl p-4 md:p-5 shadow-xl border" style={card}>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                {allPointsForts.length > 0 && (
                                                    <div className="rounded-2xl border overflow-hidden h-full" style={{ borderColor: '#bbf7d0' }}>
                                                        <div className="flex items-center gap-2 px-4 py-3 border-b" style={{ background: '#f0fdf4', borderColor: '#bbf7d0' }}>
                                                            <CheckCircle size={15} className="text-green-600" />
                                                            <span className="text-sm font-semibold text-green-900">Ce qui fonctionne bien</span>
                                                            <span className="ml-auto text-xs font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700">{allPointsForts.length}</span>
                                                        </div>
                                                        <div className="p-4 space-y-2">
                                                            {visiblePointsForts.map((p, i) => (
                                                                <div key={i} className="flex items-start gap-2 text-sm text-gray-700">
                                                                    <span className="text-green-500 mt-0.5 flex-shrink-0">✓</span>
                                                                    <span>{p}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                        {hiddenPointsForts.length > 0 && (
                                                            <div className="px-3 pb-3">
                                                                <BlurredSection
                                                                    title="Suite des points forts"
                                                                    description="Débloquez l'analyse complète pour voir tous les éléments positifs."
                                                                    ctaLabel="Prendre rendez-vous"
                                                                    ctaLink={ctaLink}
                                                                    onCtaClick={() => trackEvent({ token, eventType: 'cta_click', eventLabel: 'cta_blurred_visual_points' })}
                                                                    compact
                                                                >
                                                                    <div className="p-2 space-y-2">
                                                                        {hiddenPointsForts.map((p, i) => (
                                                                            <div key={`hidden-pf-${i}`} className="flex items-start gap-2 text-sm text-gray-700 bg-white/80 border border-green-100 rounded-lg p-3">
                                                                                <span className="text-green-500 mt-0.5 flex-shrink-0">✓</span>
                                                                                <span>{p}</span>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </BlurredSection>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                                {allAxesAmelioration.length > 0 && (
                                                    <div className="rounded-2xl border overflow-hidden h-full" style={{ borderColor: '#fde68a' }}>
                                                        <div className="flex items-center gap-2 px-4 py-3 border-b" style={{ background: '#fffbeb', borderColor: '#fde68a' }}>
                                                            <AlertTriangle size={15} className="text-amber-600" />
                                                            <span className="text-sm font-semibold text-amber-900">Axes d'amélioration</span>
                                                            <span className="ml-auto text-xs font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">{allAxesAmelioration.length}</span>
                                                        </div>
                                                        <div className="p-4 space-y-2">
                                                            {visibleAxesAmelioration.map((ax, i) => (
                                                                <div key={i} className="flex items-start gap-2 text-sm text-gray-700">
                                                                    <span className="text-amber-500 mt-0.5 flex-shrink-0">→</span>
                                                                    <span>{ax}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                        {hiddenAxesAmelioration.length > 0 && (
                                                            <div className="px-3 pb-3">
                                                                <BlurredSection
                                                                    title="Suite des améliorations"
                                                                    description="Débloquez l'audit complet pour accéder à toutes les actions prioritaires."
                                                                    ctaLabel="Prendre rendez-vous"
                                                                    ctaLink={ctaLink}
                                                                    onCtaClick={() => trackEvent({ token, eventType: 'cta_click', eventLabel: 'cta_blurred_visual_points' })}
                                                                    compact
                                                                >
                                                                    <div className="p-2 space-y-2">
                                                                        {hiddenAxesAmelioration.map((ax, i) => (
                                                                            <div key={`hidden-ax-${i}`} className="flex items-start gap-2 text-sm text-gray-700 bg-white/80 border border-amber-100 rounded-lg p-3">
                                                                                <span className="text-amber-500 mt-0.5 flex-shrink-0">→</span>
                                                                                <span>{ax}</span>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </BlurredSection>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                </>
                            )}
                        </div>
                        );
                    })()}

                    {/* ── ANALYSE IA ────────────────────────────────── */}
                    {activeTab === 'ia' && (
                        <div className="tab-panel-enter flex flex-col gap-6">
                            {!iaData && audit?.status === 'done' && (
                                <div className="rounded-2xl p-8 shadow-xl border text-center" style={card}>
                                    <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(176,160,232,0.25), rgba(157,193,234,0.2))' }}>
                                        <Sparkles size={32} className="text-[#9b6ef5]" strokeWidth={2} />
                                    </div>
                                    <h3 className="text-lg font-bold text-gray-900 mb-2">Analyse IA non disponible</h3>
                                    <p className="text-sm text-gray-600 max-w-md mx-auto flex items-center justify-center gap-2">
                                        <AlertTriangle size={16} className="text-amber-500 flex-shrink-0" />
                                        L'analyse IA est générée automatiquement lors de l'audit initial. Lancez un nouvel audit pour l'obtenir.
                                    </p>
                                </div>
                            )}

                            {iaData && (
                                <>
                                    {/* Hero Analyse IA — bloc accrocheur */}
                                    <div
                                        className="rounded-2xl overflow-hidden mb-6 relative"
                                        style={{
                                            background: 'linear-gradient(145deg, rgba(176,160,232,0.18) 0%, rgba(157,193,234,0.12) 50%, rgba(141,228,204,0.08) 100%)',
                                            border: '1px solid rgba(176,160,232,0.25)',
                                            boxShadow: '0 8px 32px rgba(176,160,232,0.08)',
                                        }}
                                    >
                                        <button
                                            type="button"
                                            onClick={() => setIaExplanationOpen(true)}
                                            className="absolute right-4 top-4 p-2 rounded-full text-neutral-400 hover:text-neutral-600 hover:bg-white/50 transition-colors z-10"
                                            aria-label="Pourquoi c'est important"
                                        >
                                            <HelpCircle size={20} />
                                        </button>
                                        <div className="p-6 md:p-8 flex flex-col md:flex-row items-center gap-6 md:gap-8">
                                            <div
                                                className="w-20 h-20 rounded-2xl flex items-center justify-center flex-shrink-0"
                                                style={{ background: 'linear-gradient(135deg, rgba(176,160,232,0.35), rgba(157,193,234,0.25))', boxShadow: '0 4px 20px rgba(176,160,232,0.2)' }}
                                            >
                                                <Sparkles size={36} style={{ color: '#7c5dd4' }} strokeWidth={2} />
                                            </div>
                                            <div className="flex-1 text-center md:text-left min-w-0">
                                                <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: '#7c5dd4' }}>
                                                    Analyse du contenu
                                                </p>
                                                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">
                                                    Ce que l'IA comprend de votre site
                                                </h2>
                                                {iaData.homepage_understanding ? (
                                                    <p className="text-sm md:text-base text-gray-700 leading-relaxed">
                                                        {iaData.homepage_understanding}
                                                    </p>
                                                ) : (
                                                    <p className="text-sm text-gray-500 italic">Aucune synthèse disponible.</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    {/* Modale Pourquoi c'est important — Analyse IA */}
                                    {iaExplanationOpen && createPortal(
                                        <div className="fixed inset-0 z-[10000]" style={{ isolation: 'isolate' }}>
                                            <div className="absolute inset-0 bg-black/50 backdrop-blur-md" aria-hidden onClick={() => setIaExplanationOpen(false)} />
                                            <div className="absolute left-1/2 top-1/2 w-full max-w-md -translate-x-1/2 -translate-y-1/2 px-4" role="dialog" aria-modal="true" aria-labelledby="help-modal-title-ia-tab">
                                                <div className="rounded-2xl shadow-2xl border border-white/80 bg-white/95 backdrop-blur-md p-6 relative" style={{ boxShadow: '0 25px 50px -12px rgba(0,0,0,0.2)' }} onClick={(e) => e.stopPropagation()}>
                                                    <button type="button" onClick={() => setIaExplanationOpen(false)} className="absolute right-3 top-3 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors" aria-label="Fermer">
                                                        <X size={20} />
                                                    </button>
                                                    <h2 id="help-modal-title-ia-tab" className="text-lg font-bold text-gray-900 pr-8 mb-3">Pourquoi c'est important</h2>
                                                    <div className="text-neutral-600 text-sm leading-relaxed">{TAB_EXPLANATIONS.ia}</div>
                                                </div>
                                            </div>
                                        </div>,
                                        document.body
                                    )}

                                    {/* Identité du site — cartes colorées */}
                                    {iaData.site_identity && (
                                        <div className="rounded-2xl p-6 md:p-8 shadow-xl border mb-6" style={card}>
                                            <h3 className="text-lg font-bold text-gray-900 mb-1 flex items-center gap-2">
                                                Identité du site
                                            </h3>
                                            <p className="text-sm text-gray-500 mb-4">Ce que l'IA a déduit de votre page d'accueil.</p>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                                                {[
                                                    { key: 'secteur', icon: FileText, label: 'Secteur', value: iaData.site_identity.secteur, color: '#9dc1ea' },
                                                    { key: 'localisation', icon: MapPin, label: 'Localisation', value: iaData.site_identity.localisation, color: '#8de4cc' },
                                                    { key: 'cible', icon: Users, label: 'Clientèle cible', value: iaData.site_identity.clientele_cible, color: '#e8c078' },
                                                    { key: 'valeur', icon: Star, label: 'Proposition de valeur', value: iaData.site_identity.proposition_valeur, color: '#b0a0e8' },
                                                ].map(({ key, icon: Icon, label, value, color }) => (
                                                    <div
                                                        key={key}
                                                        className="flex items-center gap-3 px-4 py-3 rounded-xl border backdrop-blur-sm bg-white/50 border-white/60 min-w-0"
                                                    >
                                                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${color}18` }}>
                                                            <Icon size={16} style={{ color }} />
                                                        </div>
                                                        <div className="min-w-0 flex-1">
                                                            <div className="text-xs text-gray-400 font-medium">{label}</div>
                                                            <div className="text-sm font-bold text-gray-700 leading-snug mt-0.5">{value || '—'}</div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Recommandations pour être plus visible dans les modèles IA — 2 visibles + blur + CTA */}
                                    {iaData.recommandations_visibilite_llm?.length > 0 && (() => {
                                        const allLlmRecs = [...(iaData.recommandations_visibilite_llm || []), ...FAKE_LLM_RECOMMENDATIONS];
                                        const visibleLlmRecs = allLlmRecs.slice(0, 2);
                                        const blurredLlmRecs = allLlmRecs.slice(2);

                                        const LlmRecRow = ({ rec, index, blurPx = 0 }) => (
                                            <div
                                                key={index}
                                                className="flex items-start gap-3 p-3 rounded-xl border border-white/60 bg-white/50 backdrop-blur-sm"
                                                style={blurPx ? { filter: `blur(${blurPx}px)` } : undefined}
                                            >
                                                <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 font-bold text-sm" style={{ background: 'rgba(124,93,212,0.15)', color: '#7c5dd4' }}>
                                                    {index + 1}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="font-semibold text-sm text-gray-900">{rec.title}</div>
                                                    <div className="text-xs text-gray-600 mt-0.5 leading-relaxed">{rec.explanation}</div>
                                                </div>
                                            </div>
                                        );

                                        return (
                                            <div className="rounded-2xl p-6 md:p-8 shadow-xl border mb-6" style={card}>
                                                <h3 className="text-lg font-bold text-gray-900 mb-1 flex items-center gap-2">
                                                    <Sparkles size={20} style={{ color: '#7c5dd4' }} />
                                                    Plus visible dans les modèles IA
                                                </h3>
                                                <p className="text-sm text-gray-500 mb-4">
                                                    Recommandations pour que votre site soit mieux compris et plus cité par les assistants IA (ex. Gemini, ChatGPT).
                                                </p>
                                                <div className="space-y-3">
                                                    {visibleLlmRecs.map((rec, i) => (
                                                        <LlmRecRow key={i} rec={rec} index={i} />
                                                    ))}
                                                </div>
                                                {blurredLlmRecs.length > 0 && (
                                                    <BlurredSection
                                                        title="Mon audit GEO"
                                                        description="Prenez rendez-vous pour échanger sur les points à corriger en priorité et sur la stratégie à mettre en place."
                                                        ctaLabel="Prendre rendez-vous"
                                                        ctaLink={ctaLink}
                                                        onCtaClick={() => trackEvent({ token, eventType: 'cta_click', eventLabel: 'cta_blurred_ia_llm' })}
                                                        blurAmount={0}
                                                        compact
                                                        className="mt-2"
                                                    >
                                                        <div className="space-y-3 pt-2 -mt-2">
                                                            {blurredLlmRecs.map((rec, i) => (
                                                                <LlmRecRow key={i} rec={rec} index={2 + i} blurPx={2 + i * 2} />
                                                            ))}
                                                        </div>
                                                    </BlurredSection>
                                                )}
                                            </div>
                                        );
                                    })()}

                                    {/* Fallback : uniquement homepage_understanding sans site_identity */}
                                    {!iaData.site_identity && iaData.homepage_understanding && (
                                        <div className="rounded-2xl p-6 shadow-xl border mb-6" style={card}>
                                            <h3 className="text-base font-bold text-gray-900 mb-3 flex items-center gap-2">
                                                <Eye size={20} style={{ color: '#7c5dd4' }} />
                                                Ce que l'IA a compris de votre site
                                            </h3>
                                            <p className="text-gray-700 text-sm leading-relaxed pl-5 border-l-4 italic" style={{ borderColor: '#b0a0e8' }}>
                                                "{iaData.homepage_understanding}"
                                            </p>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>

                {/* Bloc CTA + blur en bas de page */}
                <div className="mt-8 w-full">
                    
                </div>

                <p className="text-center text-xs text-gray-400 mt-10">
                    Cet audit gratuit analyse la page d'accueil de votre site. L'audit complet couvre toutes vos pages.
                </p>
                <div className="mt-3 flex justify-center">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-medium text-gray-500 bg-gray-100 border border-gray-200">
                        Audit réalisé par ascan.io
                    </span>
                </div>
            </div>
        </div>
    );
}
